import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import {
    fetchInvestigateTemplate,
    fetchPatientInvestigationRxPDF,
    UpdateInvestigateRxPdF,
    handleUpdateFrequencyQty,
    handleUpdateAIDates,
    HandleUpdateSchedule,
    handleUpdateBillingFrequency,
    handleUpdateSingleDispenseRxStatus,
    handleUpdateIsScCarryDose,
    handleUpdateTemplateNotes,
    handleUpdateScheduleDose,
    // handleMarkRefillFlexi
} from "../../services/reducers/pharmacy/dispense/investiGateRX";

import {
    LoadingView,
    ApiErrorMessage,
    Loader
} from "../../components/common";

import { UpdatePacmedTime } from "../../services/reducers/pharmacy/accountibility/accountibilityNewRx";
import { showToast } from "./helper";
import { useCustomDispatch } from "../../hooks";

import { currentDate, APP_TIMEZONE, formatDateToYYYYMMDD_CA, shiftAppDate } from "../constants/formInitialStates";
import { ModalCloseIcon } from "../../assets/svg/icons";
import DoseGapField from "../../components/pharmacy/dispense/DoseGapField";
import { useAccountibilitySelector } from "../../services/selectors/accountabilitySelectors";

import Axios from "../../networking/intraceptor";
import { update_common_sig_prescription } from "../../networking/urlEndPoints";
import {
    buildInvestigationQueue,
    readInvestigationQueueSession,
    writeInvestigationQueueSession
} from "./investigationRxQueue";
// import { FlexiDate as DateComponent } from "../../components/pharmacy/form/form";
import { PrevIcons, NextIcons } from "../../assets/svg/icons";
import { FlexiDelivery } from "../../components/pharmacy/dispense/FlexiDelivery";
import { PaymentCollection } from "../../components/pharmacy/dispense/PaymentCollection";
import * as routeNames from '../../routes/routeName'
import { handleUpdateRephraseNotes } from "../../services/reducers/pharmacy/dispense/investiGateRX";
import {
    buildRepeatedCycleRows,
    describeBaseCycle,
    describeFillWarning,
    inclusiveDaySpan
} from "./changingDoseRepeatCycle";
import { calculateFentanylChangingPatches } from "./fentanylChangingPatches";
import { needsAiChangingDoseSetup } from "./aiChangingDoseSchedule";

const LastDeliveries = React.lazy(() => import("../../components/pharmacy/dispense/investigationRX")
    .then(module => ({ default: module.LastDeliveries })));

const AUDIT_SHEET_AVAILABLE_FROM_DATE = '2026-04-23';
const STANDARD_BILLING_FREQUENCY_VALUES = ["1", "2", "7", "14", "21", "28"];
const APP_AUDIT_SHEET_DATE_OPTIONS = {
    timeZone: APP_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
};

const AUDIT_SHEET_MONTH_LOOKUP = {
    jan: '01',
    january: '01',
    feb: '02',
    february: '02',
    mar: '03',
    march: '03',
    apr: '04',
    april: '04',
    may: '05',
    jun: '06',
    june: '06',
    jul: '07',
    july: '07',
    aug: '08',
    august: '08',
    sep: '09',
    sept: '09',
    september: '09',
    oct: '10',
    october: '10',
    nov: '11',
    november: '11',
    dec: '12',
    december: '12',
};

const normalizeAuditSheetTransactionDate = (value) => {
    if (!value) return null;

    const dateText = String(value).trim();
    const isoDateMatch = dateText.match(/\d{4}-\d{2}-\d{2}/);
    if (isoDateMatch) {
        return isoDateMatch[0];
    }

    const displayDateMatch = dateText.match(/^(\d{1,2})\s+([A-Za-z]{3,9}),?\s+(\d{2}|\d{4})/);
    if (displayDateMatch) {
        const [, dayValue, monthValue, yearValue] = displayDateMatch;
        const month = AUDIT_SHEET_MONTH_LOOKUP[monthValue.toLowerCase()];
        if (month) {
            const year = yearValue.length === 2 ? `20${yearValue}` : yearValue;
            return `${year}-${month}-${String(dayValue).padStart(2, '0')}`;
        }
    }

    const parsedDate = new Date(dateText);
    if (Number.isNaN(parsedDate.getTime())) {
        return null;
    }

    return parsedDate
        .toLocaleDateString('en-CA', APP_AUDIT_SHEET_DATE_OPTIONS)
        .replace(/\//g, '-');
};

export const hasAuditSheetEligibleTransactionDate = (historyRecords) => {
    if (!Array.isArray(historyRecords)) return false;

    return historyRecords.some((record) => {
        const candidateDates = [
            record?.reef_date,
            record?.REEFDATE,
            record?.formatted_reef_date,
            record?.add_datetime,
            record?.add_datetime_display,
            record?.date_time,
        ];

        return candidateDates.some((dateValue) => {
            const normalizedDate = normalizeAuditSheetTransactionDate(dateValue);
            return normalizedDate && normalizedDate >= AUDIT_SHEET_AVAILABLE_FROM_DATE;
        });
    });
};


// Not changing doses - auto select the frequency
export const getExpectedDailyDose = (patientDetails) => {
    const prescriptionDailyDose = Number(patientDetails?.prescription_detail?.daily_dose);
    if (Number.isFinite(prescriptionDailyDose) && prescriptionDailyDose > 0) {
        return prescriptionDailyDose;
    }

    const routeDailyDose = Number(patientDetails?.route_detail?.daily_dose);
    if (Number.isFinite(routeDailyDose) && routeDailyDose > 0) {
        return routeDailyDose;
    }

    const quantity = Number(patientDetails?.prescription_detail?.quantity_authorized);
    const days = Number(patientDetails?.prescription_detail?.day_supply);
    if (Number.isFinite(quantity) && quantity > 0 && Number.isFinite(days) && days > 0) {
        return quantity / days;
    }

    return Number(patientDetails?.ddose) || 0;
};

export const handleDoseInputChange = (index,
    value,
    doseInputs,
    setDoseInputs,
    patientDetails,
    isChanging,
    frequency,
    setTotalDoseError,
    setDoseErrors) => {

    let newDoseInputs = [...doseInputs];
    const totalQty = getExpectedDailyDose(patientDetails);

    // Handle input change and validate
    if (!isNaN(value) && value !== '') {
        const currentDose = Number(value);
        newDoseInputs[index] = value;

        // Only auto-distribute if not in changing mode
        if (!isChanging) {
            // Calculate total of doses before current index
            const previousDosesTotal = newDoseInputs
                .slice(0, index)
                .reduce((sum, dose) => sum + (Number(dose) || 0), 0);

            // Calculate remaining quantity after current dose
            const remainingQty = totalQty - previousDosesTotal - currentDose;

            // If current dose is valid (doesn't exceed total remaining) 
            if (currentDose <= totalQty - previousDosesTotal) {
                // Auto-distribute remaining quantity among subsequent doses
                const remainingInputs = newDoseInputs.length - (index + 1);

                if (remainingInputs > 0) {
                    switch (frequency) {
                        case 'BID':
                            if (index === 0) {
                                newDoseInputs[1] = remainingQty.toString();
                            }
                            break;

                        case 'TID':
                            if (index === 0) {
                                const dosePerRemaining = Math.floor(remainingQty / 2);
                                newDoseInputs[1] = dosePerRemaining.toString();
                                newDoseInputs[2] = (remainingQty - dosePerRemaining).toString();
                            } else if (index === 1) {
                                newDoseInputs[2] = remainingQty.toString();
                            }
                            break;

                        case 'QID':
                            if (index === 0) {
                                const dosePerRemaining = Math.floor(remainingQty / 3);
                                newDoseInputs[1] = dosePerRemaining.toString();
                                newDoseInputs[2] = dosePerRemaining.toString();
                                newDoseInputs[3] = (remainingQty - (dosePerRemaining * 2)).toString();
                            } else if (index === 1) {
                                const dosePerRemaining = Math.floor(remainingQty / 2);
                                newDoseInputs[2] = dosePerRemaining.toString();
                                newDoseInputs[3] = (remainingQty - dosePerRemaining).toString();
                            } else if (index === 2) {
                                newDoseInputs[3] = remainingQty.toString();
                            }
                            break;

                        default:
                            break;
                    }
                }
            }
            validateDoseInputs(newDoseInputs, patientDetails, setTotalDoseError, setDoseErrors);
        }
    } else {
        // Clear current input if value is invalid
        newDoseInputs[index] = '';
        // Only clear subsequent inputs if not in changing mode
        if (!isChanging) {
            newDoseInputs = [
                ...newDoseInputs.slice(0, index + 1),
                ...newDoseInputs.slice(index + 1).map(() => '')
            ];
        }
    }

    setDoseInputs(newDoseInputs);
};

export const validateDoseInputs = (newDoseInputs,
    patientDetails,
    setTotalDoseError,
    setDoseErrors) => {

    const newErrors = []

    // Calculate total using float conversion for decimal support
    const total = newDoseInputs.reduce((sum, dose) => {
        const doseNum = dose === '' ? 0 : parseFloat(dose) || 0;
        return sum + doseNum;
    }, 0);

    const requiredTotal = getExpectedDailyDose(patientDetails);

    // Validate each dose
    newDoseInputs.forEach((dose, index) => {
        const doseNum = dose === '' ? 0 : parseFloat(dose) || 0;
        if (dose && isNaN(doseNum)) {
            newErrors[index] = 'Enter a valid number'
        } else if (doseNum < 0) {
            newErrors[index] = 'Dose cannot be negative'
        } else if (doseNum > requiredTotal) {
            newErrors[index] = ``
        } else {
            newErrors[index] = ''
        }
    })

    // Check if all doses are filled
    const allDosesFilled = newDoseInputs.every(dose => dose !== '');

    // Round to 1 decimal place for display and comparison
    const roundedTotal = Math.round(total * 10) / 10;

    if (allDosesFilled) {
        if (roundedTotal !== requiredTotal) {
            setTotalDoseError(`Total doses (${roundedTotal}) must equal exactly TOTAL QTY (${requiredTotal})`)
        } else {
            setTotalDoseError('')
        }
    } else {
        setTotalDoseError('')
    }

    const hasError = newErrors.some(error => error) ||
        (allDosesFilled && roundedTotal !== requiredTotal);

    setDoseErrors(newErrors)
    return !hasError
}

// Updating the frequency doses - changing doses
export const handleUpdateIsChangingFrequencyDoses = (frequencyRows,
    patientDetails,
    dispatch,
    formData,
    billingFrequency,
    setHasChangingDoseData,
    setIsChanging,
    setFrequencyRows,
    setFrequency,
    setDoseInputs,
    setChangingUpdateType,
    isChanging,
    onSuccess
) => {
    const duration = calculateDaysSupply(patientDetails)
    const daySupply = patientDetails?.prescription_detail?.day_supply
    const totalCycleDays = frequencyRows?.reduce((total, row) => total + Math.max(0, Number(row?.days || 0)), 0) || 0
    const ignoreSkipDays = Number(formData?.ignore_skip_days || 0)

    if (ignoreSkipDays > 0 && totalCycleDays > 0 && ignoreSkipDays >= totalCycleDays) {
        showToast('Initial Skip Days must be less than the total changing dose Days.', 'error')
        return
    }

    const frequencyData = frequencyRows?.map(row => {
        const days = isChanging ? row.days : (duration > 0 ? duration : daySupply);
        return rowToApiFreq(row, { days, isChanging });
    })

    const payload = {
        frequency: frequencyData,
        rxes_id: patientDetails?.rxdata_id,
        is_changing: isChanging ? true : false,
        is_non_dilaudid: !patientDetails?.is_frequency ? false : true,
        ignore_skip_days: ignoreSkipDays,
    }
    if (!formData?.start_date && !formData?.stop_date) {
        showToast('AI Start and Stop Date is required', 'error')
        return
    }

    dispatch(handleUpdateFrequencyQty({
        payload, setHasChangingDoseData,
        setIsChanging,
        setFrequencyRows,
        setFrequency,
        setDoseInputs,
        setChangingUpdateType,
        onSuccess
    }))
}

// Updating the frequency doses - not changing doses
export const UpdateFrequencyQty = (frequency,
    doseInputs,
    patientDetails,
    dispatch,
    formData,
    billingFrequency,
    setHasChangingDoseData,
    setIsChanging,
    setFrequencyRows,
    setFrequency,
    setDoseInputs
) => {

    const { start_date, stop_date } = formData

    const duration = calculateDaysSupply(patientDetails)
    const daySupply = patientDetails?.prescription_detail?.day_supply

    const payload = {
        frequency: [{
            frequency: frequency,
            dose_rows: (doseInputs || []).map(d => ({ dose: String(d ?? ''), iv_code: '', dose_code: '' })),
            days: duration > 0 ? duration : daySupply
        }],
        rxes_id: patientDetails?.rxdata_id,
        is_changing: false,
        is_non_dilaudid: !patientDetails?.is_frequency ? false : true
    }

    if (!frequency) {
        // alert('Please select a frequency')
        return
    }

    if (!start_date || !stop_date) {
        showToast('AI Start and Stop Date is required', 'error')
        return
    }

    // if (start_date && stop_date) {
    //     const payloadDates = {
    //         rx_number: patientDetails?.rxnumber,
    //         ai_start_date: start_date,
    //         ai_stop_date: stop_date,
    //     }
    //     dispatch(handleUpdateAIDates({
    //         requestType: 'update_aidates',
    //         payload: payloadDates,
    //         type: 'auto_update'
    //     }))
    // }

    dispatch(handleUpdateFrequencyQty({
        payload,
        setHasChangingDoseData,
        setIsChanging,
        setFrequencyRows,
        setFrequency,
        setDoseInputs
    }))
}

// Get the dose placeholder
export const getDosePlaceholder = (doseNumber) => {
    const placeholders = {
        1: '1st Dose',
        2: '2nd Dose',
        3: '3rd Dose',
        4: '4th Dose'
    }
    return placeholders[doseNumber] || `${doseNumber}th Dose`
}

// Handle the frequency selection for not changing doses
export const handleSelectFrequency = (e, setFrequency, setDoseInputs) => {
    const selectedFreq = e.target.value
    setFrequency(selectedFreq)

    const doseCount = {
        'OD': 1,
        'BID': 2,
        'TID': 3,
        'QID': 4,
        '5x': 5,
        '6x': 6,
        '7x': 7
    }[selectedFreq] || 0

    setDoseInputs(new Array(doseCount).fill(''))
}

// Handle the update of the investigate rx pdf
export const HandleUpdateInvestigateRxPDF = (rxNumber,
    templateName,
    setLoading,
    dispatch,
    options = {}) => {

    if (rxNumber && templateName) {
        setLoading(true)
        return dispatch(UpdateInvestigateRxPdF({
            rxnumber: rxNumber,
            template_data: templateName,
            setLoading,
            onPharmacistNotFound: options.onPharmacistNotFound
        }))
    }

    return Promise.resolve({ success: false, errorMessage: 'Template is required.' })
}

// --- Common frequency row structure: dose_rows (id, dose, iv_code, dose_code) ---
const DEFAULT_DOSE_ROW = () => ({ dose: '', iv_code: '', dose_code: '', send_to_winrx: false });
export const defaultFrequencyRow = () => ({
    frequency: 'OD',
    dose_rows: [{ ...DEFAULT_DOSE_ROW() }],
    days: ''
});

const normalizeWinRxDoseSelection = (doseRows = []) => {
    if (!doseRows.length) return doseRows;

    const selectedIndex = doseRows.findIndex(doseRow => doseRow?.send_to_winrx === true);

    return doseRows.map((doseRow, index) => ({
        ...doseRow,
        send_to_winrx: selectedIndex >= 0 && index === selectedIndex
    }));
};

export const fillDailyDispenseOdDose = (rows, patientDetails) => {
    const dailyDose = getExpectedDailyDose(patientDetails);
    if (dailyDose === undefined || dailyDose === null || dailyDose === '') {
        return rows;
    }

    const dailyDoseValue = String(dailyDose);
    return (rows?.length ? rows : [defaultFrequencyRow()]).map((row) => {
        if ((row?.frequency || 'OD') !== 'OD') {
            return row;
        }

        const doseRows = row?.dose_rows?.length ? row.dose_rows : [{ ...DEFAULT_DOSE_ROW() }];
        const firstDose = doseRows[0]?.dose;
        if (firstDose !== undefined && firstDose !== null && String(firstDose).trim() !== '') {
            return row;
        }

        return {
            ...row,
            frequency: 'OD',
            dose_rows: doseRows.map((doseRow, index) => (
                index === 0 ? { ...doseRow, dose: dailyDoseValue } : doseRow
            ))
        };
    });
};

const dailyDispenseOdAutoSaveKeys = new Set();

const getDailyDispenseOdAutoSaveKey = (patientDetails) => {
    const rxId = patientDetails?.rxdata_id || patientDetails?.rxnumber;
    return rxId ? String(rxId) : '';
};

export const saveDailyDispenseOdDoseFrequency = ({
    patientDetails,
    formData,
    dispatch,
    billingFrequency,
    selectedFQY,
    setSelectedFQY,
    setBillingFrequency,
    setHasChangingDoseData,
    setIsChanging,
    setFrequencyRows,
    setFrequency,
    setDoseInputs,
    setChangingUpdateType,
    requestType = 'update_frequency_qty',
    type = 'auto_update'
}) => {
    const dailyDose = getExpectedDailyDose(patientDetails);
    if (dailyDose === undefined || dailyDose === null || dailyDose === '') {
        return false;
    }

    const isPageLoadAutoSave = type === 'remove_toast';
    const autoSaveKey = isPageLoadAutoSave ? getDailyDispenseOdAutoSaveKey(patientDetails) : '';
    if (autoSaveKey && dailyDispenseOdAutoSaveKeys.has(autoSaveKey)) {
        return false;
    }

    if (selectedFQY !== '1' && typeof setSelectedFQY === 'function') {
        setSelectedFQY('1');
    }

    if (Number(billingFrequency) !== 1 && typeof setBillingFrequency === 'function') {
        setBillingFrequency('1');
        dispatch(handleUpdateBillingFrequency({
            payload: {
                rx_number: patientDetails?.rxnumber,
                billing_frequency: '1',
                is_single_dispense: 0
            },
            type: 'remove_toast'
        }));
    }

    const duration = calculateDaysSupply(patientDetails);
    const daySupply = patientDetails?.prescription_detail?.day_supply;
    const days = duration > 0 ? duration : daySupply;
    const rows = fillDailyDispenseOdDose([{
        frequency: 'OD',
        dose_rows: [{ ...DEFAULT_DOSE_ROW() }],
        days: days != null ? String(days) : ''
    }], patientDetails);

    if (typeof setFrequencyRows === 'function') {
        setFrequencyRows(rows);
    }
    if (typeof setIsChanging === 'function') {
        setIsChanging(false);
    }

    const payload = {
        frequency: rows.map(row => rowToApiFreq(row, { days, isChanging: false })),
        rxes_id: patientDetails?.rxdata_id,
        is_changing: false,
        is_non_dilaudid: !patientDetails?.is_frequency ? false : true,
        update_type: 'system'
    };

    if (autoSaveKey) {
        dailyDispenseOdAutoSaveKeys.add(autoSaveKey);
    }

    dispatch(handleUpdateFrequencyQty({
        payload,
        setHasChangingDoseData,
        setIsChanging,
        setFrequencyRows,
        setFrequency,
        setDoseInputs,
        setChangingUpdateType,
        requestType,
        type
    }));

    return true;
};

/** Normalize API frequency item (dose_rows or legacy dose/dose_code) to internal row with dose_rows */
export const apiFreqToInternalRow = (freq) => {
    if (!freq) return defaultFrequencyRow();
    let dose_rows;
    if (Array.isArray(freq.dose_rows) && freq.dose_rows.length > 0) {
        dose_rows = freq.dose_rows.map((r) => ({
            id: r.id ?? null,
            dose: String(r.dose ?? ''),
            iv_code: r.iv_code ?? r.ivcode ?? '',
            dose_code: r.dose_code ?? '',
            dose_code_updated_by: r.dose_code_updated_by ?? null,
            send_to_winrx: r.send_to_winrx === true || r.send_to_winrx === 1
        }));
    } else {
        const doses = freq.dose || [];
        const codes = freq.dose_code || freq.dose_codes || [];
        dose_rows = doses.map((d, i) => ({
            id: null,
            dose: String(d ?? ''),
            iv_code: (codes[i] ?? '') || '',
            dose_code: '',
            send_to_winrx: false
        }));
        if (dose_rows.length === 0) dose_rows = [{ ...DEFAULT_DOSE_ROW() }];
    }
    return {
        frequency: freq.frequency || 'OD',
        dose_rows: normalizeWinRxDoseSelection(dose_rows),
        days: freq.days != null ? String(freq.days) : '',
        ...(freq.cycle_no != null && { cycle_no: freq.cycle_no }),
        ...(freq.ignore_skip_days != null && freq.ignore_skip_days !== '' && { ignore_skip_days: freq.ignore_skip_days }),
        ...(freq.delivery_gap_minutes != null && freq.delivery_gap_minutes !== '' && { delivery_gap_minutes: freq.delivery_gap_minutes }),
        ...(freq.delivery_gap_source != null && freq.delivery_gap_source !== '' && { delivery_gap_source: freq.delivery_gap_source }),
        ...(freq.iv_code_updated_by != null && freq.iv_code_updated_by !== '' && { iv_code_updated_by: freq.iv_code_updated_by }),
        ...(freq.dose_code_updated_by != null && freq.dose_code_updated_by !== '' && { dose_code_updated_by: freq.dose_code_updated_by })
    };
};

/** Build API frequency payload from internal row (dose_rows → dose_rows with id, dose, iv_code) */
export const rowToApiFreq = (row, options = {}) => {
    const { days, isChanging } = options;
    return {
        frequency: row.frequency,
        dose_rows: normalizeWinRxDoseSelection(row.dose_rows || []).map(({ id, dose, iv_code, dose_code, send_to_winrx }) => ({
            ...(id != null && id !== '' && { id }),
            dose: String(dose ?? ''),
            iv_code: iv_code ?? '',
            dose_code: dose_code ?? '',
            send_to_winrx: send_to_winrx === true
        })),
        days: days != null ? days : (isChanging ? row.days : undefined),
        ...(row.cycle_no != null && row.cycle_no !== '' && { cycle_no: row.cycle_no }),
        // OD has no second dose to space out, so a gap is never sent for one.
        ...(row.frequency !== 'OD' && row.delivery_gap_minutes != null && row.delivery_gap_minutes !== ''
            && { delivery_gap_minutes: Number(row.delivery_gap_minutes) })
    };
};

// Add this new function to handle adding rows
export const handleAddRow = (setFrequencyRows, frequencyRows) => {
    setFrequencyRows([...frequencyRows, defaultFrequencyRow()]);
};

export const handleRemoveRow = async (indexToRemove, setFrequencyRows, frequencyRows, cycleNo, rxes_id) => {
    // Store the original row before removing
    const removedRow = frequencyRows[indexToRemove];
    // Remove the row
    setFrequencyRows(frequencyRows.filter((_, index) => index !== indexToRemove));
    if (cycleNo && rxes_id) {
        try {
            const payload = {
                rxes_id: rxes_id,
                cycle_no: cycleNo
            };
            const response = await Axios.delete(`dilaudid-frequency/delete-frequency`, { data: payload });
            return response;
        } catch (error) {
            // Restore the removed row by inserting it back at the original index
            const restoredRows = [
                ...frequencyRows.slice(0, indexToRemove),
                removedRow,
                ...frequencyRows.slice(indexToRemove + 1)
            ];
            setFrequencyRows(restoredRows);
            showToast('Failed to delete frequency row. Please try again.', 'error');
        }
    }
};

// Helper functions
export const calculateDailyDose = (doses) => {
    if (!doses?.length) return 0;
    return doses.reduce((sum, dose) => {
        const doseNum = parseFloat(dose) || 0;
        return parseFloat((sum + doseNum).toFixed(3));
    }, 0);
};

// export const calculateTotalDoses = (rows) => {
//     if (!rows?.length) return 0;
//     return rows.reduce((sum, row) => {
//         if (!row.doses || !row.days) return sum;
//         const dailyDose = calculateDailyDose(row.doses);
//         return parseFloat((sum + (dailyDose * Number(row.days))).toFixed(3));
//     }, 0);
// };

export const calculateTotalDoses = (rows) => {
    return rows?.reduce((acc, { days, dose_rows }) => {
        const d = parseInt(days, 10) || 0;
        const doseSum = (dose_rows || []).reduce((sum, dr) => sum + parseFloat(dr.dose || 0), 0);
        return acc + (d * doseSum);
    }, 0) || 0;
};

export const isQuantityMatching = (totalDoses, authorizedQty) => {
    return Math.abs(totalDoses - parseFloat(authorizedQty)) <= 0.001;
};

export const getQuantityAuthorizedDraftValue = (quantityAuthorized) => {
    if (quantityAuthorized === undefined || quantityAuthorized === null) {
        return '';
    }

    return String(quantityAuthorized);
};

export const validateQuantityAuthorizedDraft = ({ profileType, quantityAuthorizedDraft }) => {
    if (profileType !== 'pharmacist') {
        return {
            isValid: false,
            message: 'Only pharmacists can update quantity authorized'
        };
    }

    if (quantityAuthorizedDraft === '') {
        return {
            isValid: false,
            message: 'Please enter quantity authorized'
        };
    }

    const quantity = parseFloat(quantityAuthorizedDraft);

    if (Number.isNaN(quantity) || quantity < 0) {
        return {
            isValid: false,
            message: 'Please enter a valid quantity authorized'
        };
    }

    return {
        isValid: true,
        quantity
    };
};

export const focusQuantityAuthorizedInput = (inputRef) => {
    const input = inputRef?.current;

    if (!input) {
        return;
    }

    input.focus();
    const value = input.value;
    input.value = '';
    input.value = value;
};

export const alignChangingRowsToAuthorizedQty = (rows, authorizedQty) => {
    if (!Array.isArray(rows) || rows.length === 0) {
        return rows;
    }

    const targetQty = parseFloat(authorizedQty);
    if (Number.isNaN(targetQty)) {
        return rows;
    }

    const adjustedRows = rows.map(row => ({
        ...row,
        dose_rows: Array.isArray(row?.dose_rows)
            ? row.dose_rows.map(doseRow => ({ ...doseRow }))
            : []
    }));
    const lastRowIndex = adjustedRows.length - 1;
    const lastRow = adjustedRows[lastRowIndex];
    const lastRowDays = parseFloat(lastRow?.days);

    if (!lastRow || Number.isNaN(lastRowDays) || lastRowDays <= 0) {
        return adjustedRows;
    }

    if (!Array.isArray(lastRow.dose_rows) || lastRow.dose_rows.length !== 1) {
        return adjustedRows;
    }

    const previousRowsTotal = calculateTotalDoses(adjustedRows.slice(0, -1));
    const remainingQty = Number((targetQty - previousRowsTotal).toFixed(3));

    if (remainingQty < 0) {
        return adjustedRows;
    }

    const recalculatedDose = Number((remainingQty / lastRowDays).toFixed(3));
    adjustedRows[lastRowIndex] = {
        ...lastRow,
        dose_rows: [{
            ...lastRow.dose_rows[0],
            dose: String(recalculatedDose)
        }]
    };

    return adjustedRows;
};

export const calculateTotalDays = (rows) => {
    if (!rows?.length) return 0;
    return rows.reduce((sum, row) => sum + Number(row.days), 0);
};

export const hasEmptyFields = (row) => {
    const dose_rows = row.dose_rows || [];
    return !row.frequency ||
        !row.days ||
        dose_rows.length === 0 ||
        dose_rows.some(dr => dr.dose === '' || dr.dose == null);
};

const hasMissingDoseCode = (row) => {
    if (!row?.frequency || row.frequency === 'OD') return false;
    return (row.dose_rows || []).some(dr => !String(dr?.dose_code ?? '').trim());
};

// Helper function to format numbers - only show decimals if they exist
export const formatNumber = (num) => {
    // Convert to number and fix to 3 decimal places
    const fixed = Number(num).toFixed(3);
    // Remove trailing zeros after decimal point
    return fixed.replace(/\.?0+$/, '');
};

// Handle the preview of the template pdf
export const handlePreviewTemplatePDF = (rxNumber, templateName, dispatch) => {
    if (rxNumber && templateName) {
        return dispatch(fetchPatientInvestigationRxPDF({
            rx: rxNumber,
            requestType: 'investigation_pdf',
            templateData: templateName,
            preserveExistingData: true
        }))
    }
    return Promise.resolve()
}

// Handle the template change
export const handleTemplateChange = (e, setTemplateID, dispatch, patientDetails, setTemplateName) => {
    const templateName = e.target.value
    setTemplateID(templateName)
    dispatch(fetchInvestigateTemplate({
        templateName,
        drugName: patientDetails?.prescription_detail?.rxdrug,
        setTemplateName,
        rxNumber: patientDetails?.rxnumber
    }))
}

// ============================
// ✅ Handle Checked and Continue
export const handleCheckHasWitness = (patientDetails) => {
    const rxsig = patientDetails?.prescription_detail?.sig
    const witness = patientDetails?.prescription_detail?.witness
    const sigKeyword = ['dwi', 'daily witness']
    const isDwi = sigKeyword.some(keyword => rxsig?.toLowerCase()?.includes(keyword))
    return witness !== 'Y' && isDwi
}


// Handle Set All rxes For Investigation Existing Code
// export const handleSetAllRxesForInvestigation = (patientDetails, setTodaysInvestigationRxNumbers) => {
//     // ✅ Get the rxdate
//     const rxdate = patientDetails?.rxdate;
//     if (!rxdate) return;

//     // ✅ Get the session storage
//     const skippedrx = JSON.parse(sessionStorage.getItem("skippedrx")) || {};
//     const skipped_rx_numbers = JSON.parse(sessionStorage.getItem("skipped_rx_numbers")) || {};
//     const checked_rx_numbers = JSON.parse(sessionStorage.getItem("checked_rx_numbers")) || {};
//     console.log(skipped_rx_numbers)
//     console.log(skippedrx)

//     // If no session entry for current date
//     if (!skippedrx[rxdate]) {
//         skippedrx[rxdate] = patientDetails?.skipped_rxnumbers || [];
//         sessionStorage.setItem("skippedrx", JSON.stringify(skippedrx));
//     }

//     if (!skipped_rx_numbers[rxdate]) {
//         skipped_rx_numbers[rxdate] = [];
//         sessionStorage.setItem("skipped_rx_numbers", JSON.stringify(skipped_rx_numbers));
//     }

//     if (!checked_rx_numbers[rxdate]) {
//         checked_rx_numbers[rxdate] = [];
//         sessionStorage.setItem("checked_rx_numbers", JSON.stringify(checked_rx_numbers));
//     }

//     // ✅ Update today's investigation list
//     setTodaysInvestigationRxNumbers(skippedrx[rxdate] || []);
// }

// Handle Set All rxes For Investigation New Code
export const handleSetAllRxesForInvestigation = (patientDetails, setTodaysInvestigationRxNumbers) => {
    // ✅ Get the rxdate
    const rxdate = patientDetails?.rxdate;
    if (!rxdate) return;

    // ✅ Rebuild the queue from the server's pending list, leaving out Rx already checked in this tab
    const { checked, skipped } = readInvestigationQueueSession(rxdate);
    const queue = buildInvestigationQueue({
        pendingRxNumbers: patientDetails?.skipped_rxnumbers,
        checkedRxNumbers: checked,
        skippedRxNumbers: skipped,
    });
    writeInvestigationQueueSession(rxdate, { checked, skipped, queue });

    // ✅ Update today's investigation list
    setTodaysInvestigationRxNumbers(queue);
}

// Rx this tab just moved to with Checked / Skip and Continue. If it has already been checked
// (e.g. by a coworker while this screen was open), the page moves straight past it on load.
const CONTINUE_TARGET_KEY = 'investigateRx_continue_target';
const CONTINUE_TARGET_MAX_AGE_MS = 60 * 1000;

const goToInvestigationRx = (rx, navigate, query = '', options) => {
    sessionStorage.setItem(CONTINUE_TARGET_KEY, JSON.stringify({ rx: String(rx), at: Date.now() }));
    navigate(`${routeNames.pharmacy_investigate_rx}?rx-number=${rx}${query}`, options);
}

export const skipContinueTargetIfAlreadyChecked = (patientDetails, rxNumber, navigate) => {
    const rxStr = String(rxNumber);
    const rxdate = patientDetails?.rxdate;
    if (!rxdate || String(patientDetails?.rxnumber) !== rxStr) return;

    let target = null;
    try {
        target = JSON.parse(sessionStorage.getItem(CONTINUE_TARGET_KEY));
    } catch (error) {
        target = null;
    }
    if (target?.rx !== rxStr) return;
    sessionStorage.removeItem(CONTINUE_TARGET_KEY);
    if (Date.now() - Number(target?.at) > CONTINUE_TARGET_MAX_AGE_MS) return;

    // skipped_rxnumbers is the server's list of Rx not checked yet for this date
    const pending = (patientDetails?.skipped_rxnumbers || []).map(String);
    if (pending.includes(rxStr)) return;

    const { checked, skipped } = readInvestigationQueueSession(rxdate);
    const queue = buildInvestigationQueue({ pendingRxNumbers: pending, checkedRxNumbers: checked, skippedRxNumbers: skipped, excludeRx: rxStr });
    writeInvestigationQueueSession(rxdate, { queue });

    if (queue.length > 0) {
        goToInvestigationRx(queue[0], navigate, '', { replace: true });
    } else {
        alert("🎉 All RXs have been checked.");
    }
}

// Saves "checked" in the background. If the save fails the Rx goes back to the end of the list.
const saveInvestigationChecked = (rxNumber, rxdate) => {
    const rxStr = String(rxNumber);
    Axios.post(update_common_sig_prescription, { rx_number: rxNumber, 'is_investigated': 1 })
        .catch(() => {
            const { checked, skipped } = readInvestigationQueueSession(rxdate);
            writeInvestigationQueueSession(rxdate, {
                checked: checked.filter(item => item !== rxStr),
                skipped: [...skipped.filter(item => item !== rxStr), rxStr],
            });
            showToast(`Rx ${rxStr} was not saved as checked. It has been added back to the end of your list.`, 'error');
        });
}

// Handle Validate Fentanyl QTY
export const validateFentanylQty = (patientDetails, fentanylQty, selectedFQY) => {

    const isPatch =
        (patientDetails?.prescription_detail?.rxsig?.toLowerCase()?.includes('patch') ||
            patientDetails?.prescription_detail?.rxdrug?.toLowerCase()?.includes('patch'))

    if ((patientDetails?.is_fentanyl === 1 || isPatch) && !fentanylQty && selectedFQY !== 'changing') {
        showToast('Per Day Patches is required.', 'error')
        return false
    }

    return true
}

// Handle Validate Schedule Days
export const validateScheduleDays = (patientDetails, selectedDaysArray, billingFrequency) => {
    if (patientDetails?.route_detail?.rxprn === 'PRN' && (selectedDaysArray?.length === 0 && !billingFrequency)) {
        showToast('Please add Schedule days or Billing FQY', 'error')
        return true
    }
    return false
}

//Handle Check and continue.
export const handleChecked_Continue = (rx,
    patientDetails,
    dispatch,
    navigate,
    sigResponseData,
    ignoreSchedule,
    hasChangingDoseData,
    is_file_exist,
    ignoreIntervention,
    ignoreDateMismatch,
    selectedDaysArray,
    billingFrequency,
    fentanylQty,
    selectedFQY,
    frequencyRows,
    iamAware,
    is_dilaudid,
    formData
) => {

    const rxdate = patientDetails?.rxdate;
    if (!rxdate) return;

    // ✅ NEW: Check if this is a TSF (Transfer) case - if true, skip all validations
    const isTransferCase = CheckTransferCase(patientDetails?.date_range?.stop_date, patientDetails?.note_detail?.note);

    // ✅ ORIGINAL VALIDATIONS (only run if NOT a TSF case)
    if (!isTransferCase) {

        const validateTransfer = validateWinRxAiStopDateMismatch(patientDetails, formData)

        if (validateTransfer && !ignoreDateMismatch) {
            showToast('Stop date & AI Stop date mismatch, please fix', 'error')
            return
        }

        if (handleCheckHasWitness(patientDetails)) {
            showToast('Please add witness in WinRx first', 'error')
            return
        }

        // If the schedule is not ignored and there is no changing dose data, show the error
        if (sigResponseData) {
            if (!ignoreSchedule && !hasChangingDoseData) {
                showToast('Please add schedule first', 'error')
                return
            }
        }

        if (CheckInterventionCode(patientDetails, is_file_exist) && !ignoreIntervention) {
            showToast('Please add intervention code first', 'error')
            return
        }

        if (!selectedFQY && validateScheduleDays(patientDetails, selectedDaysArray, billingFrequency)) {
            return
        }

        if (!validateFentanylQty(patientDetails, fentanylQty, selectedFQY)) {
            return
        }

        const interventionCode = has_InterventionCode(patientDetails)

        if (interventionCode?.status === 1 && !ignoreIntervention) {
            showToast(interventionCode.message, 'error')
            return
        }

        // if (isSingleFrequency(frequencyRows, is_dilaudid) && !iamAware) {
        //     showToast('Please confirm awareness.', 'error')
        //     return
        // }
    }

    // ✅ SUCCESS ACTIONS (run for both TSF and non-TSF cases)
    const rxStr = String(rx);

    // ✅ Add to checked list, remove from skipped list
    const session = readInvestigationQueueSession(rxdate);
    const checked = session.checked.includes(rxStr) ? session.checked : [...session.checked, rxStr];
    const skipped = session.skipped.filter(item => item !== rxStr);
    writeInvestigationQueueSession(rxdate, { checked, skipped });

    // ✅ Mark UI checkbox
    const checkbox = document.getElementById('prescription_checked');
    if (checkbox) checkbox.checked = true;

    // ✅ API Call (background, so the pharmacist moves on straight away)
    saveInvestigationChecked(patientDetails.rxnumber, rxdate);

    // ✅ Find next RX - checked Rx are never picked again
    const queue = buildInvestigationQueue({
        pendingRxNumbers: [...session.queue, ...session.skipped],
        checkedRxNumbers: checked,
        skippedRxNumbers: skipped,
        excludeRx: rxStr,
    });
    writeInvestigationQueueSession(rxdate, { queue });

    if (queue.length > 0) {
        goToInvestigationRx(queue[0], navigate);
    } else {
        alert("🎉 All RXs have been checked.");
    }
};

export const checkIsFentanyl = (patientDetails) => {
    return (patientDetails?.is_fentanyl === 1
        || patientDetails?.prescription_detail?.rxsig?.toLowerCase()?.includes('patch')
        || patientDetails?.prescription_detail?.rxdrug?.toLowerCase()?.includes('patch'))
}

// ✅ Handle Skip and Continue
export const handleSkip_ContinueRxNumbers = (
    rxNumber,
    todaysInvestigationRxNumbers,
    setTodaysInvestigationRxNumbers,
    navigate,
    patientDetails,
    is_file_exist,
    ignoreIntervention,
    selectedDaysArray,
    billingFrequency,
    fentanylQty,
    selectedFQY
) => {
    const rxdate = patientDetails?.rxdate;

    if (!rxdate) return;

    // if (CheckInterventionCode(patientDetails, is_file_exist) && !ignoreIntervention) {
    //     showToast('Please add intervention code first', 'error')
    //     return
    // }

    // const interventionCode = has_InterventionCode(patientDetails)

    // if (interventionCode?.status === 1 && !ignoreIntervention) {
    //     showToast(interventionCode.message, 'error')
    //     return
    // }

    // if (!validateFentanylQty(patientDetails, fentanylQty, selectedFQY)) {
    //     return
    // }

    // if (!selectedFQY && validateScheduleDays(patientDetails, selectedDaysArray, billingFrequency)) {
    //     return
    // }

    const currentRxStr = String(rxNumber);
    const session = readInvestigationQueueSession(rxdate);
    const isAlreadyChecked = session.checked.includes(currentRxStr);

    // ✅ Move current RX to the end of the skipped list (reviewed after everything else)
    const skipped = [
        ...session.skipped.filter(item => item !== currentRxStr),
        ...(isAlreadyChecked ? [] : [currentRxStr])
    ];
    writeInvestigationQueueSession(rxdate, { skipped });

    // ✅ Find next RX - checked Rx are never picked again
    const queue = buildInvestigationQueue({
        pendingRxNumbers: [...session.queue, ...session.skipped],
        checkedRxNumbers: session.checked,
        skippedRxNumbers: skipped,
        excludeRx: currentRxStr,
    });
    writeInvestigationQueueSession(rxdate, { queue });

    // ✅ Update today's investigation list
    setTodaysInvestigationRxNumbers(queue);

    if (queue.length > 0) {
        goToInvestigationRx(queue[0], navigate, `&skipped=${currentRxStr}`);
    } else if (isAlreadyChecked) {
        alert("🎉 All RXs have been handled.");
    } else {
        showToast('No other prescriptions left to review. Please check this one.', 'error');
    }
};

// ✅ Handle Set Schedule For Non-Narcotics
const NO_SCHEDULED_DAYS = { Sunday: 0, Monday: 0, Tuesday: 0, Wednesday: 0, Thursday: 0, Friday: 0, Saturday: 0 };

// All seven days is the backend "no schedule" sentinel ("XXXXXXX", written by the AI sig check and
// not by a pharmacist), so it shows as no days ticked rather than a patch every day.
const isNoScheduleSentinel = (scheduledDays) => Boolean(scheduledDays)
    && Object.keys(NO_SCHEDULED_DAYS).every(day => Number(scheduledDays[day]) === 1);

// The day plus the days 2 and 4 days later, in week order (e.g. Monday -> Monday, Wednesday, Friday)
export const getPatchDaysStartingOn = (dayName, weekDays) => {
    const names = weekDays.map(day => day.value);
    const start = names.indexOf(dayName);
    if (start < 0) return [];
    return [0, 2, 4].map(offset => names[(start + offset) % 7]);
};

// pharmacistDaysOnly: show only the days ticked in OATRx, not the WinRx defaults
// (fentanyl Changing patch days are driven by those days only).
export const SetSchedule = ({ rxnumber, patientDetails, setSelectedDaysArray, selectedDaysArray, billingFrequency, pharmacistDaysOnly = false }) => {

    const days = [
        { value: 'Monday', label: 'Mon' },
        { value: 'Tuesday', label: 'Tue' },
        { value: 'Wednesday', label: 'Wed' },
        { value: 'Thursday', label: 'Thu' },
        { value: 'Friday', label: 'Fri' },
        { value: 'Saturday', label: 'Sat' },
        { value: 'Sunday', label: 'Sun' },
    ];

    // const [selectedDaysArray, setSelectedDaysArray] = useState([]);
    const pharmacistDays = patientDetails?.route_detail?.scheduled_days_overwrite;
    const defaultDays = pharmacistDaysOnly
        ? ((isNoScheduleSentinel(pharmacistDays) ? null : pharmacistDays) || NO_SCHEDULED_DAYS)
        : patientDetails?.route_detail?.scheduled_days;
    const dispatch = useCustomDispatch()

    useEffect(() => {
        if (defaultDays) {
            const selected = Object.entries(defaultDays)
                .filter(([day, isSelected]) => isSelected === 1)
                .map(([day]) => day);

            setSelectedDaysArray(selected);
        }
    }, [defaultDays]);

    // Checkbox toggle + API dispatch
    const handleCheckboxChange = (event) => {
        const { value, checked } = event.target;
        // Fentanyl Changing patch days are always 3 days, 2 apart: ticking a day selects that day,
        // +2 and +4 days (Mon -> Mon/Wed/Fri); unticking any day clears them.
        if (pharmacistDaysOnly) {
            const newPatchDays = checked ? getPatchDaysStartingOn(value, days) : [];
            dispatch(HandleUpdateSchedule({ payload: generateSchedulePayload(rxnumber, newPatchDays, days) }));
            setSelectedDaysArray(newPatchDays);
            return;
        }
        // Calculate the 'updated' array based on the previous state
        // but *before* actually setting the state.
        const newSelectedDays = checked
            ? [...selectedDaysArray, value] // Use the current 'selectedDaysArray' directly
            : selectedDaysArray.filter(day => day !== value); // Use the current 'selectedDaysArray' directly
        // Generate payload with the *calculated* new state
        const payload = generateSchedulePayload(rxnumber, newSelectedDays, days);
        // Dispatch the action with the payload
        dispatch(HandleUpdateSchedule({ payload }));
        // Now, update the state. This will trigger a re-render.
        // The dispatch has already happened with the correct payload.
        setSelectedDaysArray(newSelectedDays);
    };

    const generateSchedulePayload = (rxnumber, selectedDaysArray, allDaysConfig) => {
        const daysObject = {};
        allDaysConfig.forEach(day => {
            // The key in the nested 'days' object is the full day name (e.g., 'Sunday')
            daysObject[day.value] = selectedDaysArray.includes(day.value) ? 1 : 0;
        });
        return {
            rxnumber: rxnumber, // Use "rx_number" as per your specified format
            days: daysObject,
        };
    };

    const UpdateIsScCarryDose = (rxNumber, status) => {
        dispatch(handleUpdateIsScCarryDose({
            requestType: 'update_is_sc_carry_dose',
            payload: { rx_number: rxNumber, is_sc_carry_dose: status },
        }))
    }

    const isRxPrn = (patientDetails?.route_detail?.rxprn === 'PRN' && selectedDaysArray?.length === 0 && !billingFrequency) ? true : false;

    return (
        <React.Fragment>
            <div className={`text-sm ${isRxPrn ? 'border border-red-500 p-2 rounded-md' : ''}`}>
                <p className="text-[11px] mb-1 text-gray-500 leading-[0.7em]"
                    title={pharmacistDaysOnly ? 'Tick the first patch day; the next two (2 days apart) are ticked for you' : undefined}>
                    Scheduled
                </p>
                <div className="flex gap-2 items-center">
                    {days.map((day) => (
                        <label
                            key={day.value}
                            className="flex items-center cursor-pointer select-none"
                        >
                            <input
                                type="checkbox"
                                value={day.value}
                                checked={selectedDaysArray.includes(day.value)}
                                onChange={handleCheckboxChange}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-transparent focus:ring-2 dark:bg-gray-700"
                            />
                            <span className="w-full py-0 ml-1 fs-12 font-semibold text-sm text-gray-700 cursor-pointer">
                                {day.label}
                            </span>
                        </label>
                    ))}
                </div>
            </div>

            {
                patientDetails?.is_fentanyl === 1 ? null : selectedDaysArray.length === 0 ? null : (selectedDaysArray?.length !== 7) &&
                    <div className="text-sm">
                        <p className="text-[11px] mb-1 text-gray-500 leading-[0.7em]">Carry</p>
                        <input
                            onChange={(e) => UpdateIsScCarryDose(patientDetails?.rxnumber, e.target.checked)}
                            defaultChecked={patientDetails?.is_sc_carry_dose}
                            type="checkbox"
                            id="is_sc_carry_dose"
                            className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-transparent focus:ring-2" />
                    </div>
            }

        </React.Fragment>
    );
};


const isValidScheduledDays = (scheduledDays) => {
    if (!scheduledDays || Object.keys(scheduledDays).length === 0) {
        return false;
    }

    const activeCount = Object.values(scheduledDays).filter(val => val === 1).length;
    return (activeCount > 1 && activeCount <= 6);
}

const getSavedBillingFrequencyValue = (patientDetails) => {
    const rawBillingFreq = patientDetails?.billing_frequency;

    return rawBillingFreq !== null
        && rawBillingFreq !== undefined
        && String(rawBillingFreq).trim() !== ''
        && Number(rawBillingFreq) > 0
        ? String(rawBillingFreq)
        : '';
}

const hasSingleDispenseBillingFrequency = (patientDetails) => {
    const rawBillingFreq = patientDetails?.billing_frequency;

    return rawBillingFreq !== null
        && rawBillingFreq !== undefined
        && String(rawBillingFreq).trim() !== ''
        && Number(rawBillingFreq) === 0;
}

export const hasSavedBillingFrequencySelection = (patientDetails) => {
    const hasSingleDispenseFlag = Number(patientDetails?.is_single_dispense) === 1 || hasSingleDispenseBillingFrequency(patientDetails);
    const hasChangingDoseData = Boolean(patientDetails?.get_requency_data?.is_changing) || needsAiChangingDoseSetup(patientDetails);
    const hasBillingFrequency = Boolean(getSavedBillingFrequencyValue(patientDetails));
    const hasSavedSchedule = isValidScheduledDays(patientDetails?.route_detail?.scheduled_days);
    const hasSingleScheduledDay = patientDetails?.route_detail?.scheduled_days
        ? Object.values(patientDetails.route_detail.scheduled_days).filter(value => value === 1).length === 1
        : false;

    return hasSingleDispenseFlag || hasChangingDoseData || hasBillingFrequency || hasSavedSchedule || hasSingleScheduledDay;
}

export const loadSavedBillingFrequencySelection = ({ patientDetails, setBillingFrequency, setSelectedFQY, setFentanylQty }) => {
    const bfNumber = getSavedBillingFrequencyValue(patientDetails);
    const scheduledDays = isValidScheduledDays(patientDetails?.route_detail?.scheduled_days);
    const activeDays = patientDetails?.route_detail?.scheduled_days ? Object.values(patientDetails.route_detail.scheduled_days).filter(value => value === 1) : [];
    const hasSingleDispenseFlag = Number(patientDetails?.is_single_dispense) === 1 || hasSingleDispenseBillingFrequency(patientDetails);
    // Also Changing when the AI read the SIG as a changing dose and no
    // pharmacist has entered doses yet, so the Rx does not open as a flat
    // daily dose just because the steps are not saved.
    const isChanging = Boolean(patientDetails?.get_requency_data?.is_changing) || needsAiChangingDoseSetup(patientDetails);

    if (patientDetails?.prescription_detail?.fentanyl_dose) {
        setFentanylQty(patientDetails?.prescription_detail?.fentanyl_dose)
    }

    if (isChanging) {
        setSelectedFQY('changing')
        return;
    }

    if (hasSingleDispenseFlag) {
        setSelectedFQY('single')
        setBillingFrequency(bfNumber || '0')
        return;
    }

    if (bfNumber) {
        setBillingFrequency(bfNumber)
        if (bfNumber === '9999999') {
            setSelectedFQY('odd')
        } else if (STANDARD_BILLING_FREQUENCY_VALUES.includes(bfNumber)) {
            setSelectedFQY(bfNumber)
        } else {
            setSelectedFQY('custom')
        }
        return;
    }

    if (scheduledDays) {
        setSelectedFQY('odd')
        return;
    }

    if (activeDays?.length === 1) {
        setSelectedFQY('7')
        setBillingFrequency('7')
        return;
    }

    setSelectedFQY('')
}

const getValidRxDate = (value) => {
    if (!value) return null;

    if (value instanceof Date) {
        return formatDateToYYYYMMDD_CA(value) || null;
    }

    const dateValue = String(value).trim();
    if (!dateValue || dateValue.startsWith('0000-00-00')) return null;

    const dateMatch = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!dateMatch) return null;

    const [, year, month, day] = dateMatch;
    const date = new window.Date(Date.UTC(Number(year), Number(month) - 1, Number(day), 12));

    if (
        date.getUTCFullYear() !== Number(year) ||
        date.getUTCMonth() !== Number(month) - 1 ||
        date.getUTCDate() !== Number(day)
    ) {
        return null;
    }

    return `${year}-${month}-${day}`;
}

const shiftRxDate = (value, days = 0) => {
    const rxDate = getValidRxDate(value);
    if (!rxDate) return '';

    return shiftAppDate(rxDate, days);
}

const getMatchedRxContinuousStartDate = (patientDetails, processingStartDate) => {
    const createdDate = getValidRxDate(patientDetails?.rxdate);
    const dinStartDate = getValidRxDate(patientDetails?.din_analysis?.new_rx?.new_start_date);
    const continuousStartDate = dinStartDate || getValidRxDate(processingStartDate);
    const previousStopDate = continuousStartDate ? shiftRxDate(continuousStartDate, -1) : null;

    if (!previousStopDate) {
        return createdDate ? shiftRxDate(createdDate, 1) : continuousStartDate;
    }

    if (previousStopDate >= currentDate) {
        return shiftRxDate(previousStopDate, 1);
    }

    return createdDate ? shiftRxDate(createdDate, 1) : continuousStartDate;
}

export const autoUpdateBillingFrequency = ({ patientDetails, setBillingFrequency, dispatch, setSelectedFQY, setFentanylQty, suppressToast = false }) => {

    const rawBillingFreq = patientDetails?.billing_frequency;
    const ai_start_date = patientDetails?.ai_start_date;
    const ai_stop_date = patientDetails?.ai_end_date;

    const billingFrequency = Number(rawBillingFreq);
    const rxnumber = patientDetails?.rxnumber;
    const days_supply = Number(patientDetails?.prescription_detail?.day_supply);
    const refills_authorized = Number(patientDetails?.prescription_detail?.refills_authorized);

    const rxsig = patientDetails?.prescription_detail?.sig;
    const sigKeyword = ['puffs', 'puff'];
    const isPuffs = sigKeyword.some(keyword => rxsig?.toLowerCase()?.includes(keyword));
    const isDwi = rxsig?.toLowerCase()?.includes('dwi');

    const bfNumber = getSavedBillingFrequencyValue(patientDetails);

    const scheduledDays = isValidScheduledDays(patientDetails?.route_detail?.scheduled_days)
    const activeDays = patientDetails?.route_detail?.scheduled_days ? Object.values(patientDetails.route_detail.scheduled_days).filter(value => value === 1) : [];
    const hasSingleDispenseFlag = Number(patientDetails?.is_single_dispense) === 1;
    const isSameDaySingleDispense = IsSingleDispense(ai_start_date, ai_stop_date);
    const shouldUseSingleDispense = hasSingleDispenseFlag && isSameDaySingleDispense;
    const shouldClearSingleDispense = hasSingleDispenseFlag && !isSameDaySingleDispense;

    const frequency = (refills_authorized > 0 || isPuffs)
        ? days_supply
        : (((ai_start_date === ai_stop_date) && ai_start_date && ai_stop_date) || isDwi)
            ? 1
            : null;

    if (!rxnumber) return;

    if (shouldClearSingleDispense) {
        dispatch(handleUpdateSingleDispenseRxStatus({
            payload: {
                rx_number: rxnumber,
                status: 0
            }
        }));
    }

    const applyBillingFrequencySelection = (value) => {
        setBillingFrequency(value);
        if (STANDARD_BILLING_FREQUENCY_VALUES.includes(String(value))) {
            setSelectedFQY(String(value));
        } else {
            setSelectedFQY('custom');
        }
    };

    if (patientDetails?.prescription_detail?.fentanyl_dose) {
        setFentanylQty(patientDetails?.prescription_detail?.fentanyl_dose)
    }

    // const changingData = patientDetails?.get_requency_data;

    // const isChanging = (
    //     (Array.isArray(changingData) && changingData.length > 0) ||
    //     (typeof changingData === 'object' && changingData !== null && !Array.isArray(changingData) && Object.keys(changingData).length > 0)
    // );

    const isChanging = patientDetails?.get_requency_data?.is_changing ? true : false;

    if (isChanging) {
        setSelectedFQY('changing')
    } else {
        if (shouldUseSingleDispense) {
            setSelectedFQY('single')
        } else if (scheduledDays) {
            setSelectedFQY('odd')
        } else if (activeDays?.length === 1) {
            setSelectedFQY(7)
            setBillingFrequency(7)
            if (billingFrequency !== 7) {

                dispatch(handleUpdateBillingFrequency({
                    payload: {
                        rx_number: rxnumber,
                        billing_frequency: 7,
                        is_single_dispense: 0
                    },
                    type: suppressToast ? 'remove_toast' : undefined
                }));
            }
        }

        else if (bfNumber && !shouldClearSingleDispense) {
            setBillingFrequency(bfNumber)
            if (STANDARD_BILLING_FREQUENCY_VALUES.includes(bfNumber)) {
                setSelectedFQY(bfNumber)
            } else {
                setSelectedFQY('custom')
            }
        } else {
            setSelectedFQY('')
        }
    }

    if (billingFrequency && !shouldClearSingleDispense) return;

    if (frequency) {
        applyBillingFrequencySelection(frequency)
        dispatch(handleUpdateBillingFrequency({
            payload: {
                rx_number: rxnumber,
                billing_frequency: shouldUseSingleDispense ? 0 : frequency,
                is_single_dispense: shouldUseSingleDispense ? 1 : 0
            },
            type: suppressToast ? 'remove_toast' : undefined
        }));
    }
}

// ✅ Get the sstop date
/**
 * Calculate stop date from start date and duration using Canada Pacific timezone
 * @param {string|Date} start - Start date (string in YYYY-MM-DD format or Date object)
 * @param {number} duration - Duration in days (inclusive)
 * @returns {string} - Stop date in YYYY-MM-DD format (Canada Pacific timezone)
 */


// export const getStopDate = (start, duration) => {
//     const startDate = new Date(start);
//     startDate.setDate((startDate.getDate() + duration) - 1);
//     return startDate.toISOString().split('T')[0];
// }
export const getStopDate = (start, duration) => {
    if (!start || !duration || duration < 1) return '';

    // Parse start date string
    let startDateStr;
    if (typeof start === 'string') {
        startDateStr = start;
    } else if (start instanceof Date) {
        // Format the date in Pacific timezone first
        const formatter = new Intl.DateTimeFormat('en-CA', {
            timeZone: APP_TIMEZONE,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
        startDateStr = formatter.format(start);
    } else {
        return '';
    }

    if (!startDateStr || startDateStr === '0000-00-00') return '';

    // Parse date components
    const parts = startDateStr.split('-');
    if (parts.length !== 3) return '';

    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);

    if (isNaN(year) || isNaN(month) || isNaN(day)) return '';

    // Create date object using local timezone constructor (represents the Pacific date)
    // Use noon to avoid DST edge cases
    const startDate = new Date(year, month - 1, day, 12, 0, 0);

    // Add (duration - 1) days to get end date (inclusive)
    // If duration = 1, end_date = start_date
    // If duration = 2, end_date = start_date + 1 day
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + (duration - 1));

    // Format the end date in Pacific timezone
    const endFormatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: APP_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });

    // Format using the end date components
    return endFormatter.format(endDate);
};


// ✅ Handle Auto Update AI Dates
export const handleAutoUpdateAiDates = ({
    ai_start_date,
    ai_stop_date,
    rx_start_date,
    rx_stop_date,
    setFormData,
    patientRxHistory,
    dispatch,
    patientDetails,
    lastAutoUpdateAiDatesRef,
}) => {
    // return;
    const rxnumber = patientDetails?.rxnumber;
    let newrx_start_date = null;

    // const refills_authorized = Number(patientDetails?.prescription_detail?.refills_authorized);
    // const days_supply = Number(patientDetails?.prescription_detail?.day_supply);

    let processedRxStartDate; // ✅ Processed RX start date
    let processingStartDate; // ✅ Processing start date

    // ✅ Check if the AI dates are available
    const aiStartDate = getValidRxDate(ai_start_date);
    const aiStopDate = getValidRxDate(ai_stop_date);
    const hasAiDates = aiStartDate && aiStopDate;

    // ✅ Check if the Winrx RX dates are available
    const rxStartDateValue = getValidRxDate(rx_start_date);
    const rxStopDateValue = getValidRxDate(rx_stop_date);
    const hasRxDates = rxStartDateValue && rxStopDateValue;

    if (!rxnumber) return;
    // ✅ Case 1: Do nothing if AI dates already exist
    if (hasAiDates) return;

    const setAutoDateFormData = (nextDates) => {
        setFormData((prev) => {
            const hasChanged = Object.entries(nextDates).some(([key, value]) => prev?.[key] !== value);
            return hasChanged ? { ...prev, ...nextDates } : prev;
        });
    };

    const dispatchAutoUpdateAiDates = (payload) => {
        const autoUpdateKey = [
            payload.rx_number,
            payload.ai_start_date || '',
            payload.ai_stop_date || '',
        ].join('|');

        if (lastAutoUpdateAiDatesRef?.current === autoUpdateKey) {
            return false;
        }

        if (lastAutoUpdateAiDatesRef) {
            lastAutoUpdateAiDatesRef.current = autoUpdateKey;
        }

        dispatch(handleUpdateAIDates({
            requestType: 'update_aidates',
            payload,
            type: 'auto_update'
        }));

        return true;
    };

    // ✅ Get the Start Date from the Patient Rx History
    if (patientRxHistory?.length > 0) {
        processedRxStartDate = patientRxHistory[patientRxHistory?.length - 1];
        processingStartDate = getValidRxDate(processedRxStartDate?.REEFDATE);
    }
    const isMatchedRxNumber = MatchedRxNumber(patientDetails);
    if (isMatchedRxNumber) {
        newrx_start_date = getMatchedRxContinuousStartDate(patientDetails, processingStartDate);
    }

    const resolvedRxStartDate = (
        rxStartDateValue
        || processingStartDate
    );

    const rxStartDate = resolvedRxStartDate
        || (isMatchedRxNumber ? newrx_start_date : null);

    // ✅ If the start date is available
    if (rxStartDate !== '0000-00-00' && rxStartDate) {
        // ✅ Get the duration
        // const duration = (refills_authorized > 0 ? ((refills_authorized + 1) * days_supply) : days_supply)
        const duration = calculateDaysSupply(patientDetails);
        // ✅ Get the stop date
        const stopDate = getStopDate(rxStartDate, duration);
        // ✅ Set the form data
        setAutoDateFormData({
            start_date: rxStartDate,
            stop_date: stopDate
        });

        dispatchAutoUpdateAiDates({
            rx_number: rxnumber,
            ai_start_date: rxStartDate,
            ai_stop_date: stopDate
        });
        return;
    }

    // ✅ Case 2: Use RX dates if available
    else if (hasRxDates) {
        // console.log(rx_start_date, rx_stop_date)
        const adjustedStopDate = shiftRxDate(rxStopDateValue, -1);
        if (!adjustedStopDate) return;

        setAutoDateFormData({
            start_date: rxStartDateValue,
            stop_date: adjustedStopDate
        });

        const payload = {
            rx_number: rxnumber,
            ai_start_date: rxStartDateValue,
            ai_stop_date: adjustedStopDate,
        };

        dispatchAutoUpdateAiDates(payload);
        return;
    }
    // ✅ Case 3: If no AI or RX dates, fallback to last entry in patientRxHistory
    else if (patientRxHistory?.length > 0) {
        // console.log(ai_start_date, ai_stop_date)
        if (!processingStartDate) return;
        setAutoDateFormData({
            start_date: processingStartDate,
        })

        const payload = {
            rx_number: rxnumber,
            ai_start_date: processingStartDate,
        }

        dispatchAutoUpdateAiDates(payload);
    }
};

// ✅ Check Transfer Case
export const CheckTransferCase = (stopDate, note) => {
    // const rx_stop_date = new window.Date(stopDate);
    // const todayDate = new window.Date(currentDate);
    const rx_stop_date = stopDate
    const todayDate = currentDate
    const isTransfer = note && note?.toLowerCase()?.startsWith('->');
    // console.log(rx_stop_date, todayDate, isTransfer)
    // console.log(currentDate, 'currentDate')
    // console.log(stopDate, 'stopDate')

    if (rx_stop_date <= todayDate && isTransfer) {
        return true
    }
    return false
}

// ✅ Is Single Dispense
export const IsSingleDispense = (rxstartdate, rxstopdate) => {
    if (!rxstartdate || !rxstopdate) return false
    const startDate = getValidRxDate(rxstartdate);
    const stopDate = getValidRxDate(rxstopdate);
    if (startDate && stopDate && startDate === stopDate) {
        return true
    }
    return false
}

// ✅ Handle Get Changing Dose Data
export const handleGetChangingDoseData = ({ patientDetails,
    setIsChanging,
    setFrequencyRows,
    setFrequency,
    setDoseInputs,
    setHasChangingDoseData,
    setChangingUpdateType,
}) => {

    // A changing schedule is still owed for this Rx: the system's flat daily
    // row is not its schedule, so it is not loaded as one. This also keeps
    // "Please add schedule first" in force until the steps are saved.
    if (needsAiChangingDoseSetup(patientDetails)) {
        setHasChangingDoseData(false)
        return;
    }

    let frequencyData;

    if (patientDetails?.get_requency_data) {
        frequencyData = patientDetails?.get_requency_data;
    } else if (patientDetails?.data) {
        frequencyData = patientDetails?.data;
    }

    if (frequencyData?.length === 0 || !frequencyData) {
        setHasChangingDoseData(false)
        return;
    }

    setHasChangingDoseData(true)

    const isChanging = frequencyData?.is_changing;
    const freqList = frequencyData?.frequency;
    const updatedAtDate = frequencyData?.updated_at_date;
    const updatedAtTime = frequencyData?.updated_at_time;
    const updateType = frequencyData?.update_type === 'system' && `Updated by System on ${updatedAtDate} at ${updatedAtTime}`;

    if (isChanging) {
        const getFrequencyRows = freqList?.map(freq => apiFreqToInternalRow(freq));
        setFrequencyRows(getFrequencyRows);
        setIsChanging(true);
    } else if (freqList?.length > 0) {
        const firstFreq = freqList?.[0];
        setIsChanging(false);
        setFrequencyRows([apiFreqToInternalRow(firstFreq)]);
    }

    if (typeof setChangingUpdateType === 'function') {
        setChangingUpdateType(updateType)
    }
}

// export const isDose = (pacmed_overwrite) => {
//     if (!pacmed_overwrite || typeof pacmed_overwrite !== 'object') return [];

//     const doses = Object.values(pacmed_overwrite)
//         .filter(value => value > 0)
//         .map(value => value.toString());

//     if (doses.length === 0) return [];

//     const frequencyMap = { 1: "OD", 2: "BID", 3: "TID", 4: "QID", 5: "5x", 6: "6x", 7: "7x" };
//     const frequency = frequencyMap[doses.length] || "";

//     return frequency ? [{ frequency, doses }] : [];
// }

export const handleFQYFrequencyRowsChanges = ({
    patientDetails,
    setFrequencyRows,
    setIsChanging,
    selectedFQY,
    is_dilaudid,
    pacmed_overwrite
}) => {

    // const hasDose = isDose(pacmed_overwrite)
    const frequencyData = patientDetails?.get_requency_data;
    const isChanging = frequencyData?.is_changing;
    const hasFrequency = Array.isArray(frequencyData?.frequency) && frequencyData.frequency.length > 0;

    const defaultRow = [defaultFrequencyRow()];

    const mappedFrequencyData = hasFrequency
        ? frequencyData.frequency.map(item => apiFreqToInternalRow(item))
        : defaultRow;

    if (selectedFQY === 'changing') {
        setFrequencyRows(isChanging ? mappedFrequencyData : defaultRow);
        setIsChanging(true);
    } else if (Number(selectedFQY) === 1) {

        // if(hasDose?.length > 0){
        //     setFrequencyRows(hasDose)
        //     setIsChanging(false)
        //     return
        // }

        // setFrequencyRows(isChanging ?  defaultRow : mappedFrequencyData);
        // setIsChanging(false);
        // console.log(mappedFrequencyData)
        setFrequencyRows(fillDailyDispenseOdDose(isChanging ? defaultRow : mappedFrequencyData, patientDetails));
        setIsChanging(false);

        // setFrequencyRows(isChanging ? defaultRow : (mappedFrequencyData && (mappedFrequencyData[0]?.doses[0] !== '')) ? mappedFrequencyData : (hasDose && is_dilaudid && hasDose?.length > 0) ? hasDose : defaultRow);
        // setIsChanging(false);
    }
}

/**
 * Analyzes medtimes data and determines frequency/doses based on active medication times
 * @param {Array} rxpacmedData - Array of medtime objects with value property
 * @returns {Object|null} - { frequency: string, doses: string[], activeCount: number } or null if invalid
 */
export const analyzeMedtimesForFrequency = (rxpacmedData) => {
    if (!Array.isArray(rxpacmedData) || rxpacmedData.length === 0) {
        return null;
    }

    // Entries arrive in chronological order: rxpacmed_all_slots is sent sorted
    // by the backend (0500 first) and the legacy 4-slot rxpacmed_data is
    // already chronological among its slots. Order matters — the first active
    // dose of the day is the one stamped VS (witnessed); the rest get VSUF.
    const activeDoses = rxpacmedData
        .map(medTime => Number(medTime?.value || 0))
        .filter(dose => dose > 0);

    const activeCount = activeDoses.length;

    // Map active count to frequency — mirrors backend safeSupplyFrequencyLabel().
    // OD (once daily) and 5x/6x are valid safe-supply frequencies; previously
    // this only accepted BID/TID/QID and skipped morning-only doses, leaving
    // those Rxs blank for manual entry.
    const frequencyMap = {
        1: 'OD',
        2: 'BID',
        3: 'TID',
        4: 'QID',
        5: '5x',
        6: '6x',
        7: '7x'
    };

    const frequency = frequencyMap[activeCount];

    // No active doses (or more slots than we can label) — caller falls back
    // to the daily-dispense OD flow driven by the daily dose.
    if (!frequency) {
        return null;
    }

    return {
        frequency,
        doses: activeDoses.map(dose => dose.toString()),
        activeCount
    };
}

/**
 * Auto-sets frequency and doses based on medtimes data
 * @param {Object} params - Configuration object
 * @param {Object} params.patientDetails - Patient details object
 * @param {Array} params.frequencyRows - Current frequency rows state
 * @param {Function} params.setFrequencyRows - State setter for frequency rows
 * @param {string} params.selectedFQY - Current selected FQY
 * @param {Function} params.setSelectedFQY - State setter for selected FQY
 * @param {string} params.billingFrequency - Current billing frequency
 * @param {Function} params.setBillingFrequency - State setter for billing frequency
 * @param {Object} params.formData - Form data with start_date and stop_date
 * @param {Function} params.dispatch - Redux dispatch function
 * @param {Function} params.setHasChangingDoseData - State setter for changing dose data
 * @param {Function} params.setIsChanging - State setter for isChanging flag
 * @param {Function} params.setFrequency - State setter for frequency
 * @param {Function} params.setDoseInputs - State setter for dose inputs
 * @param {Function} params.setChangingUpdateType - State setter for changing update type
 * @returns {boolean} - Returns true if auto-set was performed, false otherwise
 */
export const autoSetFrequencyFromMedtimes = ({
    patientDetails,
    frequencyRows,
    setFrequencyRows,
    selectedFQY,
    setSelectedFQY,
    billingFrequency,
    setBillingFrequency,
    formData,
    dispatch,
    setHasChangingDoseData,
    setIsChanging,
    setFrequency,
    setDoseInputs,
    setChangingUpdateType
}) => {
    // Prefer the chronological all-slots payload (includes 0500/1400) when the
    // backend provides it; fall back to the legacy 4-slot rxpacmed_data.
    const allSlots = patientDetails?.route_detail?.rxpacmed_all_slots;
    const medtimesSlots = (Array.isArray(allSlots) && allSlots.length > 0)
        ? allSlots
        : patientDetails?.route_detail?.rxpacmed_data;

    // Early return conditions
    if (!Array.isArray(medtimesSlots) || medtimesSlots.length === 0) {
        return false;
    }

    // Check if frequency and doses are already set (API/state use dose_rows, not doses)
    const firstRow = frequencyRows?.[0];
    const doseValues = firstRow?.dose_rows?.map((dr) => dr?.dose) ?? firstRow?.doses ?? [];

    const hasExistingFrequency =
        firstRow?.frequency &&
        String(firstRow.frequency).trim() !== '' &&
        Array.isArray(doseValues) &&
        doseValues.length > 0 &&
        doseValues.some((dose) => dose !== '' && dose != null && String(dose).trim() !== '');

    if (hasExistingFrequency) {
        return false;
    }

    // Analyze medtimes to determine frequency and doses
    const medtimesAnalysis = analyzeMedtimesForFrequency(medtimesSlots);


    if (!medtimesAnalysis) {
        return saveDailyDispenseOdDoseFrequency({
            patientDetails,
            formData,
            dispatch,
            billingFrequency,
            selectedFQY,
            setSelectedFQY,
            setBillingFrequency,
            setHasChangingDoseData,
            setIsChanging,
            setFrequencyRows,
            setFrequency,
            setDoseInputs,
            setChangingUpdateType,
            type: 'remove_toast'
        });
    }

    const { frequency, doses } = medtimesAnalysis;

    // Validate we have valid frequency and doses
    if (!frequency || !Array.isArray(doses) || doses.length === 0) {
        return false;
    }

    // Set frequency rows with analyzed data
    // setFrequencyRows([{
    //     frequency,
    //     dose_rows: (doses || []).map(d => ({ dose: String(d ?? ''), iv_code: '' })),
    //     days: '',
    //     cycle_no: ''
    // }]);

    // Set selected FQY to Daily Dispense (1)
    if (selectedFQY !== '1') {
        setSelectedFQY('1');
    }

    // Update billing frequency to 1 (Daily Dispense)
    if (Number(billingFrequency) !== 1) {
        setBillingFrequency('1');
        dispatch(handleUpdateBillingFrequency({
            payload: {
                rx_number: patientDetails?.rxnumber,
                billing_frequency: '1',
                is_single_dispense: 0
            },
            type: 'remove_toast'
        }));
    }


    // Update frequency quantity via API (only if dates are available)
    if (formData?.start_date && formData?.stop_date) {
        const duration = calculateDaysSupply(patientDetails);
        const daySupply = patientDetails?.prescription_detail?.day_supply;
        const days = duration > 0 ? duration : daySupply;

        const payload = {
            frequency: [{
                frequency,
                dose_rows: (doses || []).map(d => ({ dose: String(d ?? ''), iv_code: '', dose_code: '' })),
                days: days || daySupply
            }],
            rxes_id: patientDetails?.rxdata_id,
            is_changing: false,
            is_non_dilaudid: !patientDetails?.is_frequency ? false : true,
            update_type: 'system'
        };

        dispatch(handleUpdateFrequencyQty({
            payload,
            setHasChangingDoseData,
            setIsChanging,
            setFrequencyRows,
            setFrequency,
            setDoseInputs,
            setChangingUpdateType,
            requestType: 'update_frequency_qty',
            type: 'remove_toast'
        }));
    }

    return true;
}

// ✅ Handle Get Non Changing Dose Data






// ✅ Update Frequency Button
export const UpdateFrequencyButton = ({
    frequencyRows,
    patientDetails,
    dispatch,
    formData,
    billingFrequency,
    setHasChangingDoseData,
    setIsChanging,
    setFrequencyRows,
    setFrequency,
    setDoseInputs,
    frequencyUpdateResponse,
    handleUpdateIsChangingFrequencyDoses,
    setChangingUpdateType,
    durationInput,
    isChanging,
    onUpdateSuccess
}) => {

    const is_fentanyl = patientDetails?.is_fentanyl === 1;
    const day_supply = (Number(patientDetails?.prescription_detail?.day_supply) || Number(durationInput));
    const totalDays = calculateTotalDays(frequencyRows);

    const calculateDoses = calculateTotalDoses(frequencyRows)
    const authorizedQty = Number(patientDetails?.prescription_detail?.quantity_authorized)

    const NonChangingTotalQty = getExpectedDailyDose(patientDetails);
    const NonChangingTotalDoses = frequencyRows?.reduce((acc, curr) => {
        const doseSum = (curr.dose_rows || [])
            .reduce((a, dr) => a + (parseFloat(dr.dose) || 0), 0);
        return acc + doseSum;
    }, 0);
    const shouldRequireDoseCode = patientDetails?.rxsafesupply !== 'Y' && Number(patientDetails?.is_frequency) !== 1;
    const hasMissingRequiredDoseCode = shouldRequireDoseCode && frequencyRows?.some(hasMissingDoseCode);

    // Final disabled logic
    const isDisabled = is_fentanyl
        ? (frequencyUpdateResponse?.loading || day_supply !== totalDays || hasMissingRequiredDoseCode)
        : ((!isQuantityMatching(calculateDoses, authorizedQty)) || frequencyUpdateResponse?.loading || hasMissingRequiredDoseCode);

    const isDisabledNonChanging = (!isQuantityMatching(NonChangingTotalDoses, NonChangingTotalQty) || frequencyUpdateResponse?.loading || hasMissingRequiredDoseCode)

    const handleClick = () => {
        if (isChanging) {
            if (is_fentanyl && day_supply !== totalDays) {
                showToast('Total days must match day supply', 'error');
                return;
            }
            if (!isQuantityMatching(calculateDoses, authorizedQty) && !is_fentanyl) {
                showToast('Total doses must equal authorized quantity', 'error');
                return;
            }
        }

        handleUpdateIsChangingFrequencyDoses(
            frequencyRows,
            patientDetails,
            dispatch,
            formData,
            billingFrequency,
            setHasChangingDoseData,
            setIsChanging,
            setFrequencyRows,
            setFrequency,
            setDoseInputs,
            setChangingUpdateType,
            isChanging,
            onUpdateSuccess
        );
    };

    return (
        <>
            {isChanging ?
                <button
                    onClick={handleClick}
                    disabled={isDisabled}
                    className={`px-4 py-2 w-fit rounded text-sm text-white uppercase ${isDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-700 hover:bg-blue-500'
                        }`}
                >
                    {frequencyUpdateResponse?.loading ? 'Updating...' : 'Update'}
                </button>
                :
                <button
                    onClick={handleClick}
                    disabled={isDisabledNonChanging}
                    className={`px-4 py-2 w-fit rounded text-sm text-white uppercase ${isDisabledNonChanging ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-700 hover:bg-blue-500'
                        }`}
                >
                    {frequencyUpdateResponse?.loading ? 'Updating...' : 'Update'}
                </button>
            }
        </>
    );
};

//NOn
export const DilaudidStatusMessage = ({ doseInputs, patientDetails, changingUpdateType }) => {

    const totalDays = calculateDaysSupply(patientDetails)
    const duration = totalDays > 0 ? totalDays : patientDetails?.prescription_detail?.day_supply

    const totalQty = getExpectedDailyDose(patientDetails);
    const totalDoses = doseInputs?.reduce((acc, curr) => acc + parseFloat(curr), 0)

    if (doseInputs?.some(dose => dose === '')) {
        return (
            <div className="text-red-600 text-[11px] italic w-full col-span-12 -my-1">
                All fields (Doses) are required for each row
            </div>
        );
    }

    if (!isQuantityMatching(totalDoses, totalQty)) {
        return (
            <div className="text-red-600 text-[11px] italic w-full col-span-12 -my-1">
                Total doses ({formatNumber(totalDoses)}) must equal authorized quantity ({formatNumber(totalQty)})
            </div>
        );
    }

    return (
        <div className="text-green-600 text-[11px] italic w-full col-span-12 -my-1">
            {formatNumber(totalDoses)} doses calculated for {duration} day(s)
            <span className="underline lowercase italic ml-2 font-normal">
                {changingUpdateType}
            </span>
        </div>
    );
};


/**
 * Deep-ish copy of the frequency rows so the undo snapshot cannot be disturbed
 * by later in-place edits to a row or one of its dose_rows.
 */
const snapshotFrequencyRows = (rows) => (Array.isArray(rows) ? rows : []).map(row => ({
    ...row,
    dose_rows: Array.isArray(row?.dose_rows) ? row.dose_rows.map(doseRow => ({ ...doseRow })) : row?.dose_rows
}));

/**
 * ✅ "Repeat this pattern" control for the Changing FQY panel.
 *
 * The pharmacist types one cycle of a repeating SIG (e.g. 3 tablets for 2 days
 * then 2.5 tablets for 1 day) and this repeats it until the authorized quantity
 * is consumed. Rows it produces are ordinary rows — editable, removable, and
 * identical in shape to hand-entered ones — so the existing UPDATE handler and
 * validation rule are untouched.
 */
export const RepeatCycleFillControls = ({
    frequencyRows,
    setFrequencyRows,
    patientDetails,
    formData,
    isChanging
}) => {
    const [undoSnapshot, setUndoSnapshot] = useState(null);
    const [warning, setWarning] = useState('');

    // Changing mode only. Fentanyl balances against day supply rather than the
    // authorized quantity, so it keeps its existing behaviour untouched.
    if (!isChanging || patientDetails?.is_fentanyl === 1) return null;

    const hasFilled = undoSnapshot !== null;

    // Only offer the button once the rows actually form a usable cycle. This is
    // the fill's own row validation (describeBaseCycle), not a second rule, so
    // the button can never appear in a state where the fill would reject the
    // rows. A blank row therefore shows nothing — the existing "all fields are
    // required" line is what tells the pharmacist to keep typing.
    const showRepeat = !hasFilled && describeBaseCycle(frequencyRows).ok;

    // After a fill only Undo is offered; Repeat would be dead weight because the
    // rows already total the authorized quantity.
    const showUndo = hasFilled;

    if (!showRepeat && !showUndo) return null;

    const handleRepeat = () => {
        // Stop-date guard: only applied when both AI dates are present and parse.
        const maxTotalDays = inclusiveDaySpan(formData?.start_date, formData?.stop_date);

        const result = buildRepeatedCycleRows({
            rows: frequencyRows,
            authorizedQty: patientDetails?.prescription_detail?.quantity_authorized,
            maxTotalDays
        });

        if (!result?.ok) {
            setWarning('');
            showToast(result?.message || 'The pattern could not be repeated.', 'error');
            return;
        }

        setUndoSnapshot(snapshotFrequencyRows(frequencyRows));
        setFrequencyRows(result.rows);
        setWarning(describeFillWarning(result));
    };

    const handleUndo = () => {
        if (!undoSnapshot) return;
        setFrequencyRows(undoSnapshot);
        setUndoSnapshot(null);
        setWarning('');
    };

    return (
        <div className="w-full mb-1">
            <div className="flex flex-wrap items-center gap-2">
                {showRepeat && (
                    <button
                        type="button"
                        onClick={handleRepeat}
                        title="Repeat the rows above as a cycle until the authorized quantity is used up"
                        className="px-3 py-1 rounded text-[11px] uppercase border border-blue-700 text-blue-700 hover:bg-blue-50"
                    >
                        Repeat this pattern
                    </button>
                )}

                {showUndo && (
                    <button
                        type="button"
                        onClick={handleUndo}
                        title="Restore the rows exactly as they were before the fill"
                        className="px-3 py-1 rounded text-[11px] uppercase border border-gray-400 text-gray-700 hover:bg-gray-100"
                    >
                        Undo fill
                    </button>
                )}
            </div>

            {warning && (
                <div className="text-amber-700 text-[11px] italic mt-1">
                    {warning}
                </div>
            )}
        </div>
    );
};

// ✅ Changing Dose Status Message
export const ChangingDoseStatusMessage = ({ frequencyRows,
    patientDetails,
    changingUpdateType,
    durationInput,
    isChanging,
    setIamAware,
    iamAware,
    is_dilaudid,
    updatedScheduleDose
}) => {

    const totalDoses = calculateTotalDoses(frequencyRows);
    const calculatedFentanylPatches = calculateFentanylChangingPatches(frequencyRows);
    const authorizedQty = parseFloat(patientDetails?.prescription_detail?.quantity_authorized);
    const totalDays = calculateTotalDays(frequencyRows);
    const is_fentanyl = patientDetails?.is_fentanyl === 1;
    const day_supply = (Number(patientDetails?.prescription_detail?.day_supply) || Number(durationInput));

    const NonChangingTotalQty = getExpectedDailyDose(patientDetails);

    const NonChangingTotalDoses = frequencyRows?.reduce((acc, curr) => {
        const doseSum = (curr.dose_rows || [])
            .reduce((a, dr) => a + (parseFloat(dr.dose) || 0), 0);
        return acc + doseSum;
    }, 0);
    const shouldRequireDoseCode = patientDetails?.rxsafesupply !== 'Y' && Number(patientDetails?.is_frequency) !== 1;
    const hasMissingRequiredDoseCode = shouldRequireDoseCode && frequencyRows?.some(hasMissingDoseCode);


    // const handleUpdateIamAware = (e) => {
    //     setIamAware(e.target.checked)
    // }

    // const hasSingleFrequency = isSingleFrequency(frequencyRows, is_dilaudid)

    if (isChanging) {
        if (is_fentanyl) {
            if (day_supply !== totalDays) {
                return (
                    <div className="text-red-600 text-[11px] italic mb-1 uppercase">
                        Day supply ({day_supply}) must equal total days ({totalDays})
                    </div>
                );
            }
            return (
                <div className="text-green-600 text-[11px] italic mb-1 uppercase">
                    {formatNumber(calculatedFentanylPatches)} Patches calculated for {day_supply} day(s)
                    <span className="underline lowercase italic ml-2 font-normal">
                        {changingUpdateType}
                    </span>
                </div>
            );
        }

        if (frequencyRows.some(hasEmptyFields)) {
            return (
                <div className="text-red-600 text-[11px] italic mb-1 uppercase">
                    All fields (Frequency, Doses, and Days) are required for each row
                </div>
            );
        }

        if (hasMissingRequiredDoseCode) {
            return (
                <div className="text-red-600 text-[11px] italic mb-1 uppercase">
                    Witness/Carry is required
                </div>
            );
        }

        if (!isQuantityMatching(totalDoses, authorizedQty)) {
            return (
                <div className="text-red-600 text-[11px] italic mb-1 uppercase">
                    Total doses ({formatNumber(totalDoses)}) must equal authorized quantity ({formatNumber(authorizedQty)})
                </div>

            );
        }

        return (<>

            {Boolean(updatedScheduleDose) && frequencyRows?.length === 1 && NonChangingTotalDoses !== parseFloat(updatedScheduleDose) && !Number(frequencyRows[0]?.cycle_no) && (
                <div className="text-red-600 text-[11px] italic mb-1 uppercase">
                    Dose doesn't match the scheduled dose
                </div>
            )}

            <div className="text-green-600 text-[11px] italic mb-1 uppercase">
                {formatNumber(totalDoses)} doses calculated for {totalDays} day(s)
                <span className="underline lowercase italic ml-2 font-normal">
                    {changingUpdateType}
                </span>
            </div>

            {/* {hasSingleFrequency && (
                <div className={`flex gap-2 items-center btnnn mb-1 bg-gray-200 px-2 py-1 rounded  w-fit ${iamAware ? '' : 'border border-red-500'}`}>
                    <input
                        onChange={(e) => handleUpdateIamAware(e)}
                        // defaultChecked={patientDetails?.signature?.signature_required === 1}
                        defaultChecked={iamAware}
                        id="iam_aware"
                        type="checkbox"
                        value=""
                        className="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-transparent dark:ring-offset-gray-800 focus:ring-0 focus:ring-offset-0" />
                    <label htmlFor="iam_aware" className=" text-black !text-[13px]  uppercase">I’m aware.</label>
                </div>
            )} */}

        </>
        );
    }
    else {

        if (frequencyRows?.some((value) => (value.dose_rows || []).some(dr => dr.dose === '' || dr.dose == null))) {
            return (
                <div className="text-red-600 text-[11px] italic mb-1 uppercase">
                    All fields (Doses) are required for each row
                </div>
            );
        }
        if (hasMissingRequiredDoseCode) {
            return (
                <div className="text-red-600 text-[11px] italic mb-1 uppercase">
                    Witness/Carry is required
                </div>
            );
        }
        if (!isQuantityMatching(NonChangingTotalDoses, NonChangingTotalQty)) {
            return (
                <div className="text-red-600 text-[11px] italic mb-1 uppercase">
                    Total doses ({formatNumber(NonChangingTotalDoses)}) must equal authorized quantity ({formatNumber(NonChangingTotalQty)})
                </div>
            );
        }

        return (
            <div className="text-green-600 text-[11px]  mb-1 uppercase">
                {formatNumber(NonChangingTotalDoses)} doses calculated for {day_supply} day(s)
                <span className="underline lowercase italic ml-2 font-normal">
                    {changingUpdateType}
                </span>


                {/* {hasSingleFrequency && (
                    <div className={`flex gap-2 items-center btnnn mb-1 bg-gray-200 px-2 py-1 rounded  w-fit ${iamAware ? '' : 'border border-red-500'}`}>
                        <input
                            onChange={(e) => handleUpdateIamAware(e)}
                            // defaultChecked={patientDetails?.signature?.signature_required === 1}
                            defaultChecked={iamAware}
                            id="iam_aware"
                            type="checkbox"
                            value=""
                            className="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-transparent dark:ring-offset-gray-800 focus:ring-0 focus:ring-offset-0" />
                        <label htmlFor="iam_aware" className=" text-black !text-[13px]  uppercase">I’m aware.</label>
                    </div>
                )} */}
            </div>
        );
    }
};


// ✅ Handle Auto Update Schedule Days
export const handleAutoUpdateScheduleDays = (setSelectedDaysArray, dispatch, patientDetails) => {
    const rxnumber = patientDetails?.rxnumber;
    const billingFrequency = Number(patientDetails?.billing_frequency);
    const defaultScheduledDays = patientDetails?.route_detail?.scheduled_days;

    if (!rxnumber) return;
    if (!defaultScheduledDays) return;
    const isDefaultDaysChecked = Object.values(defaultScheduledDays)?.some(value => Number(value) === 1)
    if (isDefaultDaysChecked) return;
    if (Number(billingFrequency) === 1) {
        const defaultDays = {
            "Monday": 1,
            "Tuesday": 1,
            "Wednesday": 1,
            "Thursday": 1,
            "Friday": 1,
            "Saturday": 1,
            "Sunday": 1
        }
        const selected = Object.entries(defaultDays)
            .filter(([day, isSelected]) => isSelected === 1)
            .map(([day]) => day);

        setSelectedDaysArray(selected)

        const payload = {
            "rxnumber": rxnumber,
            "days": defaultDays
        }
        dispatch(HandleUpdateSchedule({ payload }))
    }
}


// ✅ Set Billing Frequency
export const SetBillingFrequency = ({ billingFrequency,
    selectedDaysArray,
    setBillingFrequency,
    dispatch,
    patientDetails
}) => {

    return (<>

        <div>
            <p className="text-[11px] mb-1 text-gray-500 leading-[0.7em]">Billing Fqy</p>
            <div className="relative">
                <div className={`flex h-[42px] overflow-hidden border rounded 
                    ${billingFrequency ? ' border-gray-300' :
                        (selectedDaysArray?.length === 7 || selectedDaysArray?.length === 0) ? 'border-red-500' : 'border-gray-300'} w-[155px]`}>

                    <input
                        value={billingFrequency}
                        disabled={selectedDaysArray?.length === 0 ? false : selectedDaysArray?.length !== 7}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value === '0' || /^0\d+/.test(value)) return;
                            setBillingFrequency(value);

                            // Only allow digits
                            if (!/^\d*$/.test(value)) return;

                            // Clear any existing timeout
                            if (window.billingFrequencyTimeout) {
                                clearTimeout(window.billingFrequencyTimeout);
                            }

                            // Set new timeout
                            window.billingFrequencyTimeout = setTimeout(() => {
                                if (!value) {
                                    showToast('Billing Frequency is required.', 'error');
                                    return;
                                }

                                if (!/^\d+$/.test(value)) {
                                    showToast('Billing Frequency must be a number.', 'error');
                                    return;
                                }

                                dispatch(handleUpdateBillingFrequency({
                                    payload: {
                                        rx_number: patientDetails?.rxnumber,
                                        billing_frequency: value,
                                        is_single_dispense: 0
                                    }
                                }));
                            }, 1000); // 1 second delay
                        }}
                        type="number"
                        className={`w-full px-2 py-1 text-gray-700 focus:ring-2 focus:ring-transparent focus:outline-none border-none 
                        ${selectedDaysArray?.length === 0 ? '' : selectedDaysArray?.length !== 7 ? 'cursor-not-allowed bg-gray-100' : ''}`}
                        inputMode="numeric"
                        pattern="\d*"
                    />
                    <span className={`px-2 flex items-center text-sm ${selectedDaysArray?.length === 0 ? '' : selectedDaysArray?.length !== 7 ? 'cursor-not-allowed bg-gray-100' : ''} text-gray-700 border-l border-l-gray-200`}>
                        Days
                    </span>
                </div>
                {!billingFrequency && (selectedDaysArray?.length === 7 || selectedDaysArray?.length === 0) &&
                    <p className="text-red-600  pb-2 text-[10px] dark:text-red-400">Required*</p>}
            </div>
        </div>


    </>)
}

// ✅ Check Intervention Code
export const CheckInterventionCode = (patientDetails, is_file_exist) => {
    const interventionCodeRaw = patientDetails?.note_detail?.note || '';
    const is_narcotic = patientDetails?.is_narcotic;
    const interventionCode = interventionCodeRaw
        .replace(/\s+/g, '')          // remove all whitespace
        .replace(/—/g, '-')           // normalize em-dash
        .toUpperCase();               // normalize case

    const notewords = ['IV-/', 'IV:SA', 'IV:VSUFSA', 'UFVS'];

    const containsKeyword = notewords.some(word =>
        interventionCode.includes(word.replace(/\s+/g, '').toUpperCase())
    );

    return (is_file_exist === 0 && containsKeyword && is_narcotic === 0);
};

export const has_InterventionCode = (patientDetails) => {

    const rxsig = patientDetails?.prescription_detail?.sig || '';
    const is_narcotic = patientDetails?.is_narcotic;
    const intervention_code = patientDetails?.prescription_detail?.intervention_code
    const witness = patientDetails?.prescription_detail?.witness === 'Y' ? true : false
    const note = patientDetails?.note_detail?.note || '';

    const group1_din = patientDetails?.is_sa_vs
    const sigKeywords = ['dwi', 'daily witness'];

    // ✅ Skip this check only if note contains both VS and SA as separate words (case-insensitive)
    const noteHasVS = /\bVS\b/i.test(note);
    const noteHasSA = /\bSA\b/i.test(note);
    const skipDueToNote = (noteHasVS || noteHasSA);

    const isWitnessDose = sigKeywords.some(keyword =>
        rxsig.toLowerCase().includes(keyword)
    );

    if (group1_din && is_narcotic === 1) return false;

    if (!group1_din && is_narcotic === 1 && !intervention_code) {
        if (skipDueToNote) return false;
        const expectedCode = (isWitnessDose || witness) ? 'VS' : 'SA';
        if (intervention_code !== expectedCode) {
            return {
                message: `${expectedCode} intervention code is missing.`,
                status: 1
            };
        }
        return false;
    }

    // if (is_narcotic === 0 && !intervention_code) {
    //     return { message: 'Missing intervention code for investigation RX', status: 1 };
    // }

    return false;
};


// ✅ Transaction History
export const TransactionHistory = ({ patientRxHistoryResponse, patientRxHistoryLoading, teamStatus, rxnumber, patientDetails, rxStockData }) => {

    // Delivery proof viewer state
    const [showDeliveryProofViewer, setShowDeliveryProofViewer] = useState(false);
    const [deliveryProofImage, setDeliveryProofImage] = useState(null);
    const [loadingDeliveryProof, setLoadingDeliveryProof] = useState(false);

    // Handle delivery proof click
    const handleDeliveryProofClick = (proofData) => {
        if (!proofData?.image_base64) return;

        setLoadingDeliveryProof(true);
        setShowDeliveryProofViewer(true);

        // Handle both base64 string and data URL format
        const imageData = proofData.image_base64.startsWith('data:')
            ? proofData.image_base64
            : `data:${proofData.mime_type || 'image/jpeg'};base64,${proofData.image_base64}`;

        setDeliveryProofImage(imageData);
        setLoadingDeliveryProof(false);
    };

    // Close delivery proof viewer
    const closeDeliveryProofViewer = () => {
        setShowDeliveryProofViewer(false);
        setDeliveryProofImage(null);
    };

    return <>
        {patientRxHistoryLoading && <div className="text-center text-gray-500">
            <div className="flex items-center justify-center h-[130px]">
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-gray-900"></div>
                <span className="ml-2">Loading</span>
            </div>
        </div>}

        {patientRxHistoryResponse?.length > 0 && <>
            <div className="bg-white  border border-gray-200 mb-4 rounded-xl overflow-hidden">
                <div className="bg-gradient-to-r from-gray-100 to-gray-100 px-4 py-2 border-b border-gray-200 flex justify-between">
                    <h3 className="text-lg font-semibold text-gray-800">Transaction History</h3>
                    {/* <h3 className="text-lg font-semibold text-gray-800">Last 7 deliveries</h3> */}
                    <React.Suspense fallback={null}>
                        <LastDeliveries rxnumber={rxnumber} patientDetails={patientDetails} teamStatus={teamStatus} />
                    </React.Suspense>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full table-auto text-left text-sm">
                        <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                            <tr>
                                <th className="px-4 py-2 w-[4%] text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                    <div className="flex items-center">
                                        <span className="bg-gray-200 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">#</span>
                                    </div>
                                </th>
                                <th className="px-4 py-2 w-[11%] text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                    <div className="flex items-center">
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                                        </svg>
                                        Date
                                    </div>
                                </th>
                                <th className="px-4 py-2 w-[9%] text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                    <div className="flex items-center">
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                        </svg>
                                        Time
                                    </div>
                                </th>
                                <th className="px-4 py-2 text-center w-[5%] text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                    <div className="flex items-center justify-center">
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                                        </svg>
                                        Qty
                                    </div>
                                </th>
                                <th className="px-4 py-2 text-center w-[12%] text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                    <div className="flex items-center justify-center">
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                                        </svg>
                                        Prep
                                    </div>
                                </th>
                                <th className="px-4 py-2 text-center w-[15%] text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                    <div className="flex items-center justify-center">
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                        </svg>
                                        Status
                                    </div>
                                </th>
                                <th className="px-4 py-2 w-[3%] text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                    <div className="flex items-center">
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
                                        </svg>
                                        Flexi
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {patientRxHistoryResponse?.map((value, index) => {
                                const { REUSERID, REQTY, REREASON, reef_time, dose_qty, formatted_reef_date, reef_date, quantity_change_reason_text, delivered_rx_reason_change_review_text } = value;
                                const rereasonStruck = REREASON != null && String(REREASON).trim() !== '';
                                const quantityChangeReasonText = quantity_change_reason_text != null ? String(quantity_change_reason_text).trim() : '';
                                const deliveredRxReasonChangeReviewText = delivered_rx_reason_change_review_text != null ? String(delivered_rx_reason_change_review_text).trim() : '';

                                const deliveryData = value?.delivery_prep_data || {};
                                const pharmacist = deliveryData?.prep_check?.pharmacist;
                                const assistant = deliveryData?.prep_check?.assistant;

                                const pharmacistDate = deliveryData?.prep_check?.pharmacist_date;
                                const assistantDate = deliveryData?.prep_check?.assistant_date;

                                const prepByTime = pharmacistDate ? pharmacistDate : assistantDate;
                                const prepQty = deliveryData?.prep_check?.prep_dose ? deliveryData?.prep_check?.prep_dose : '';
                                const deliverStatus = (deliveryData?.delivery_status === 'Daily Delivery' || deliveryData?.delivery_status === 'Emergency') ? ''
                                    : deliveryData?.delivery_status?.slice(0, 1).toUpperCase() ?? '';

                                const prepBy = assistant && pharmacist ? `${pharmacist}/${assistant}` : pharmacist || assistant;
                                const pharmacistName = deliveryData?.pharmacist_name;
                                const assistantName = deliveryData?.driver_name;

                                const emergencyDay = value?.day_wise_doses?.delivery_status === 'Emergency' ? true : false;
                                const dayname = value?.day_name;

                                const overwrite_dose = deliveryData?.prep_check?.overwrite_dose;
                                const overwrite_name = (deliveryData?.prep_check?.pharmacist_last_name
                                    || deliveryData?.prep_check?.assistant_last_name)

                                const overwrite_pharmacist = deliveryData?.prep_check?.pharmacist_last_name;
                                const overwrite_assistant = deliveryData?.prep_check?.assistant_last_name;

                                const qtyCutLineClass =
                                    'pointer-events-none absolute left-1/2 top-1/2 z-20 h-px w-4 -translate-x-1/2 -translate-y-1/2 bg-red-700';

                                return (
                                    <React.Fragment key={index}>
                                        <tr className={`transition-all duration-200 group ${rereasonStruck ? '[&>td]:align-middle' : 'align-top'}`}>
                                            <td className="px-4 py-2">
                                                <div className="flex items-center">
                                                    <span className="bg-blue-100 text-blue-800 text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center  transition-colors">
                                                        {patientRxHistoryResponse.length - index}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-2">
                                                <div className="flex items-center">
                                                    <span className="text-sm font-medium text-gray-900">{formatted_reef_date}</span>
                                                </div>
                                                <p className="text-xs">{dayname}</p>
                                            </td>
                                            <td className="px-4 py-2">
                                                <div className="flex items-center">
                                                    {value?.child_rows?.length > 0 ? (
                                                        <span className="text-gray-400 text-sm">-</span>
                                                    ) : (
                                                        <>
                                                            <span className="text-sm font-medium ">{REUSERID} {reef_time}</span>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                            <td className={`px-4 py-2 text-center${rereasonStruck ? ' relative overflow-visible' : ''}`}>
                                                <div className="inline-flex items-center justify-center">
                                                    {rereasonStruck ? (
                                                        <span
                                                            aria-hidden
                                                            className={qtyCutLineClass}
                                                        />
                                                    ) : null}
                                                    <span className="text-sm font-semibold  rounded-full">
                                                        {dose_qty ? dose_qty : REQTY}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-2 text-center">
                                                <div className="flex flex-col items-center">
                                                    {
                                                        // teamStatus === 1 ? 
                                                        <>
                                                            {
                                                                prepQty ? <>
                                                                    <div className="flex items-center space-x-1 mb-1">
                                                                        {/* <span className="text-sm font-medium ">{prepQty}</span> */}

                                                                        <span className="text-sm font-medium">
                                                                            {(overwrite_dose !== prepQty && overwrite_name) ? (
                                                                                <>
                                                                                    <span className="line-through text-red-500 font-normal">{prepQty}</span>{" "}
                                                                                    <span className="text-green-500 font-semibold">{overwrite_dose}</span>
                                                                                </>
                                                                            ) : (
                                                                                prepQty
                                                                            )}
                                                                        </span>

                                                                        <span className="">-</span>
                                                                        {overwrite_name ? (
                                                                            <>
                                                                                <span className="text-sm font-semibold line-through">
                                                                                    {overwrite_pharmacist}{overwrite_assistant && overwrite_pharmacist && '/'}{overwrite_assistant}
                                                                                </span>
                                                                                <span className="text-sm font-normal">{pharmacist || assistant}</span>
                                                                            </>
                                                                        ) : (
                                                                            <span className="text-sm font-medium ">{prepBy}</span>
                                                                        )}
                                                                        {emergencyDay && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">E</span>}
                                                                    </div>
                                                                    {prepByTime &&
                                                                        <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">{prepByTime}</span>
                                                                    }
                                                                </>
                                                                    : !prepQty && <>
                                                                        <div className="flex items-center space-x-1 mb-1">
                                                                            <span className="text-sm font-medium ">{prepBy && 'SYS'}</span>
                                                                            <span className="">{prepBy && '-'}</span>
                                                                            <span className="text-sm font-medium ">{prepBy}</span>
                                                                        </div>
                                                                        {prepByTime &&
                                                                            <span className="text-xs   text-gray-500 bg-gray-50 px-2 py-1 rounded">{prepByTime}</span>
                                                                        }
                                                                    </>
                                                            }
                                                        </>
                                                            // : prepByTime &&
                                                            // <span className="text-xs  text-gray-500 bg-gray-50 px-2 py-1 rounded">{prepByTime}</span>

                                                    }
                                                </div>
                                            </td>

                                            <td className="px-4 py-2 text-center">
                                                <div className="flex flex-col items-center">
                                                    {rereasonStruck ? (
                                                        <span className="text-xs font-medium bg-red-100 text-red-800 px-2 py-0.5 rounded-full">{REREASON}</span>
                                                    ) : deliverStatus === 'D' && teamStatus === 1 ? (
                                                        <>
                                                            <div className="flex items-center space-x-1 mb-1">
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                    {deliverStatus}
                                                                </span>
                                                                {(pharmacistName || assistantName) && <span className="text-gray-500">-</span>}
                                                                <span className="text-sm font-medium ">
                                                                    {assistantName && pharmacistName ? `${pharmacistName}/${assistantName}` : pharmacistName || assistantName}
                                                                </span>
                                                            </div>
                                                            {deliveryData?.delivery_date &&
                                                                <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">{deliveryData?.delivery_date}</span>
                                                            }
                                                        </>
                                                    ) : (
                                                        deliverStatus &&
                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${deliverStatus === 'P' ? 'bg-yellow-100 text-yellow-800' :
                                                            deliverStatus === 'D' ? 'bg-green-100 text-green-800' :
                                                                'bg-red-100 text-red-800'
                                                            }`}>
                                                            {deliverStatus}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            <td className={`px-4 py-2${rereasonStruck ? '' : ' align-top'}`}>
                                                {rereasonStruck ? null : (
                                                    value?.proof_of_delivery &&
                                                        typeof value.proof_of_delivery === 'object' &&
                                                        value.proof_of_delivery !== null &&
                                                        value.proof_of_delivery.image_base64 &&
                                                        typeof value.proof_of_delivery.image_base64 === 'string' &&
                                                        value.proof_of_delivery.image_base64.trim() !== '' ? (
                                                        <button
                                                            onClick={() => handleDeliveryProofClick(value.proof_of_delivery)}
                                                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:border-blue-300 transition-colors whitespace-nowrap"
                                                        >
                                                            Delivery Proof
                                                        </button>
                                                    ) : (
                                                        <div className="flex flex-col items-center gap-2">
                                                            {((deliverStatus === 'P' || (deliverStatus === 'D' && deliveryData?.flexi_delivery)) ? (
                                                                <FlexiDelivery
                                                                    flexiDeliveryData={patientRxHistoryResponse}
                                                                    value={value}
                                                                    index={index}
                                                                    patientDetails={patientDetails}
                                                                    rxStockData={rxStockData}
                                                                    reef_date={reef_date}
                                                                    currentDate={currentDate}
                                                                    deliveryStatus={deliverStatus === 'P' ? 'pending' : deliverStatus === 'D' ? 'delivered' : deliverStatus}
                                                                />
                                                            ) : null)}

                                                            {/* Payment Collection Information */}
                                                            <PaymentCollection
                                                                paymentStatus={value?.delivery_prep_data?.payment_status}
                                                                paymentComment={value?.delivery_prep_data?.payment_comment}
                                                            />
                                                        </div>
                                                    )
                                                )}
                                            </td>
                                        </tr>

                                        {quantityChangeReasonText && (
                                            <tr className="bg-amber-50 border-l-4 border-amber-300">
                                                <td className="px-4 py-2" colSpan={7}>
                                                    <div className="flex items-center gap-2 text-xs">
                                                        <span className="tracking-wide text-amber-800">
                                                            Quantity Change Reason:
                                                        </span>
                                                        <span className="text-amber-900">
                                                            {quantityChangeReasonText}
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}

                                        {deliveredRxReasonChangeReviewText && (
                                            <tr className="bg-red-50 border-l-4 border-red-300">
                                                <td className="px-4 py-2" colSpan={7}>
                                                    <div className="flex items-center gap-2 text-xs">
                                                        <span className="tracking-wide text-red-800">
                                                            Delivered RX Reason Change:
                                                        </span>
                                                        <span className="text-red-900">
                                                            {deliveredRxReasonChangeReviewText}
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}

                                        {value?.child_rows && value?.child_rows?.length > 0 &&
                                            <tr className="bg-gray-100 border-l-4 border-blue-300">
                                                <td className="px-4 py-2 w-full" colSpan={7}>
                                                    <div className="flex flex-wrap gap-2">
                                                        {value?.child_rows?.map((childRow, childIndex) => {
                                                            const pharmacist = childRow?.delivered_by_pharmacist_initials;
                                                            const assistant = childRow?.delivered_by_assistant_initials;
                                                            const childby = pharmacist && assistant ? `${pharmacist}/${assistant}` : pharmacist || assistant;
                                                            const processBy = childRow?.processed_by_initials;

                                                            return (
                                                                <div key={`${index}-child-${childIndex}`} className='flex items-center bg-white border border-blue-200 rounded-lg px-2 py-1.5   space-x-1'>
                                                                    <span className="text-sm font-semibold text-gray-800">{childRow.dose}</span>
                                                                    {(childRow?.processed_time || processBy) && (
                                                                        <span className="text-xs text-gray-600 font-medium  bg-gray-100 px-1 py-0.5 rounded-md">{childRow.processed_time} {processBy}</span>
                                                                    )}
                                                                    {childRow?.delivery_status && (
                                                                        <span className={` inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium 
                                                                        ${childRow.delivery_status?.slice(0, 1)?.toUpperCase() === 'P' ? 'bg-yellow-100 text-yellow-800' :
                                                                                childRow.delivery_status?.slice(0, 1)?.toUpperCase() === 'D' ? 'bg-green-100 text-green-800' :
                                                                                    'bg-red-100 text-red-800'
                                                                            }`}>
                                                                            {childRow?.delivery_status?.slice(0, 1)?.toUpperCase()}
                                                                        </span>
                                                                    )}

                                                                    {(childRow?.delivery_time || childby) && (
                                                                        <span className="text-xs text-gray-600  font-medium  bg-gray-100 px-1 py-0.5 rounded-md">{childRow.delivery_time} {childby}</span>
                                                                    )}
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                </td>
                                            </tr>
                                        }
                                    </React.Fragment>
                                )
                            })}
                        </tbody>
                    </table>
                </div>

                {(!patientRxHistoryResponse || patientRxHistoryResponse.length === 0) && (
                    <div className="text-center py-12">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                        </svg>
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No transactions found</h3>
                        <p className="mt-1 text-sm text-gray-500">No medication history available for this patient.</p>
                    </div>
                )}
            </div>
        </>
        }

        {/* Full Screen Delivery Proof Viewer Modal */}
        {showDeliveryProofViewer && (
            <div
                className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm"
                onClick={closeDeliveryProofViewer}
            >
                <div
                    className="relative w-full h-full flex items-center justify-center p-4"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Close Button */}
                    <button
                        onClick={closeDeliveryProofViewer}
                        className="absolute top-4 right-4 z-10 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-colors"
                        aria-label="Close delivery proof viewer"
                    >
                        <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    {/* Loading State */}
                    {loadingDeliveryProof && (
                        <div className="flex flex-col items-center justify-center">
                            <Loader />
                            <p className="mt-4 text-white text-lg">Loading delivery proof...</p>
                        </div>
                    )}

                    {/* Delivery Proof Image */}
                    {!loadingDeliveryProof && deliveryProofImage && (
                        <div className="w-full h-full flex items-center justify-center">
                            <img
                                src={deliveryProofImage}
                                alt="Delivery Proof"
                                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                                onError={() => {
                                    console.error('Error loading delivery proof image');
                                    showToast('Failed to load delivery proof image', 'error');
                                    closeDeliveryProofViewer();
                                }}
                            />
                        </div>
                    )}

                    {/* Error State - No Image Available */}
                    {!loadingDeliveryProof && !deliveryProofImage && (
                        <div className="flex flex-col items-center justify-center text-white">
                            <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-lg">No delivery proof image available</p>
                            <button
                                onClick={closeDeliveryProofViewer}
                                className="mt-4 px-4 py-2 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    )}
                </div>
            </div>
        )}
    </>
}

// ✅ Prescription PDF Preview
export const RXChart = ({ investigateRXresponse, showChartModal, closeDayWiseDoseChart }) => {

    const dayWiseDosesResponse = investigateRXresponse?.rxDayWiseDoses
    const dayWiseDosesData = dayWiseDosesResponse?.data?.data?.date_wise_data
    const winrxCurrentDate = dayWiseDosesResponse?.data?.data?.doses_generated_at

    // Handle Escape key to close modal
    useEffect(() => {
        const handleEscapeKey = (event) => {
            if (event.key === 'Escape' && showChartModal) {
                closeDayWiseDoseChart();
            }
        };

        // Add event listener when modal is open
        if (showChartModal) {
            document.addEventListener('keydown', handleEscapeKey);
        }

        // Cleanup event listener
        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [showChartModal, closeDayWiseDoseChart]);

    // Before the table:
    // const hasW = Array.isArray(dayWiseDosesData) && dayWiseDosesData.some(d => parseFloat(d?.witness) > 0);
    // const hasC = Array.isArray(dayWiseDosesData) && dayWiseDosesData.some(d => parseFloat(d?.carry) > 0);

    return <>
        {showChartModal && (
            <div className="fixed top-0 left-0 right-0 bottom-0 z-50 flex items-center justify-center p-6 overflow-x-hidden overflow-y-auto bg-black bg-opacity-50">
                <div className="relative w-full max-w-6xl max-h-[80vh] overflow-y-auto">
                    <div className="relative bg-white rounded-lg shadow">
                        <div className='flex items-center justify-between p-4 border-b rounded-t'>
                            <h3 className="text-xl font-semibold text-gray-900">
                                Day Wise Doses {dayWiseDosesResponse?.data?.data?.total_qty_left > 0 ? `- Current WinRx Qty Left (${dayWiseDosesResponse?.data?.data?.total_qty_left})` : ''}
                                {winrxCurrentDate && <p className="text-xs text-gray-500">Last synced on {winrxCurrentDate}</p>}
                            </h3>

                            <button onClick={closeDayWiseDoseChart} type="button" className="absolute top-3 right-2.5 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ml-auto inline-flex justify-center items-center">
                                <ModalCloseIcon />
                            </button>
                        </div>
                        <div className="px-6 py-4">
                            {dayWiseDosesResponse?.loading ? (
                                <div className='flex items-center justify-center my-10'><LoadingView /></div>
                            ) : dayWiseDosesResponse?.error !== null ? (
                                <div className='flex items-center justify-center my-10'><ApiErrorMessage className='' /></div>
                            ) : dayWiseDosesData?.length === 0 ? (
                                <div className='flex items-center justify-center my-10'>No Record Found</div>
                            ) : (
                                <div className="border border-gray-300 mb-2 rounded-lg">
                                    <table className="min-w-full table-auto overflow-hidden rounded-lg text-left text-sm">
                                        <thead className="bg-[#E7E7E7] text-xs text-gray-700 uppercase">
                                            <tr>
                                                <th className="px-4 py-2">DAY</th>
                                                <th className="px-4 py-2">DATE</th>
                                                <th className="px-4 py-2 text-center">DOSE</th>
                                                {/* {hasW && <th className="px-4 py-2 text-center">W</th>}
                                                {hasC && <th className="px-4 py-2 text-center">C</th>} */}
                                                <th className="px-4 py-2 text-center">W</th>
                                                <th className="px-4 py-2 text-center">C</th>
                                                <th className="px-0 py-2 text-center whitespace-nowrap">REFILL LEFT</th>
                                                <th className="px-0 py-2 text-center whitespace-nowrap">REFILL LEFT AFTER DELIVERY</th>
                                                <th className="px-0 py-2 text-center whitespace-nowrap">QTY LEFT</th>
                                                <th className="px-0 py-2 text-center whitespace-nowrap">QTY LEFT AFTER DELIVERY</th>
                                                <th className="px-4 py-2 text-center ">Emergency</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {dayWiseDosesData?.map((dose, index) => (
                                                <tr key={index} className={`${currentDate === dose.dose_date ? 'font-medium bg-yellow-100' : 'hover:bg-blue-100'}`}>
                                                    <td className="border border-l-0 border-gray-300 px-4 py-2 ">{dose.dayname}</td>
                                                    <td className={`border border-l-0 border-gray-300 px-4 py-2 whitespace-nowrap`}>{dose.formatted_dose_date}</td>

                                                    <td className="border border-gray-300 px-4 py-2 text-center">{formatNumberSmart(dose.dose)}</td>
                                                    {/* {hasW && <td className="border border-gray-300 px-4 py-2 text-center">{dose.witness}</td>}
                                                    {hasC && <td className="border border-gray-300 px-4 py-2 text-center">{dose.carry}</td>} */}

                                                    <td className="border border-gray-300 px-4 py-2 text-center">{dose.witness}</td>
                                                    <td className="border border-gray-300 px-4 py-2 text-center">{dose.carry}</td>
                                                    <td className="border border-gray-300 px-0 py-2 text-center">{formatNumberSmart(dose.refills_left)}</td>
                                                    <td className="border border-gray-300 px-0 py-2 text-center">{formatNumberSmart(dose?.refills_left_after_delivery)}</td>
                                                    <td className="border border-r-0 border-gray-300 px-0 py-2 text-center">{formatNumberSmart(dose.qty_left)}</td>
                                                    <td className="border border-r-0 border-gray-300 px-0 py-2 text-center">{formatNumberSmart(dose.qty_left_after_delivery)}</td>
                                                    <td className="border border-r-0 border-gray-300 px-4 py-2 text-center">{dose.is_emergency === 1 && 'Yes'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        )}
    </>
}

export const formatNumberSmart = (num) => {
    if (num == null || num === '') return '';
    const n = Number(num);
    if (isNaN(n)) return num;
    return n % 1 === 0 ? n.toString() : n.toFixed(2).replace(/\.?0+$/, '');
}

/**
 * Global edit access for Investigate Rx Prescription Details page.
 * Can update if admin_module === 1 OR user type is pharmacist; otherwise view-only.
 * @param {Object} profileData - User profile from useCustomSelectors (profileData)
 * @param {number} adminModule - Admin module flag from useCustomSelectors (adminModule), 1 = has access
 * @returns {{ canEdit: boolean, viewOnlyClassName: string }}
 */
export const getInvestigateRxEditAccess = (profileData, adminModule) => {
    const canEdit = (adminModule === 1 || profileData?.type === 'pharmacist');
    return {
        canEdit,
        viewOnlyClassName: canEdit ? '' : 'investigate-rx-view-only',
    };
}


const MatchedRxNumber = (details) => {
    const rxnumber = details?.rxnumber;
    const new_rxnumber = details?.din_analysis?.new_rx?.rxnum;
    if (rxnumber === new_rxnumber) {
        return true;
    }
    return false;
}

// ✅ Common function to create clickable RX link
export const createClickableRxLink = (rxNumber, routeNames) => {
    return (
        <span
            className="font-semibold cursor-pointer"
            onClick={() => {
                const url = `${routeNames.pharmacy_investigate_rx}?rx-number=${rxNumber}`;
                window.open(url, '_blank');
            }}
        >
            {rxNumber}
        </span>
    );
};

// export const getRxStatusText = (details) => {
//     // Check if details.din_analysis exists
//     const patientDetails = details?.din_analysis;

//     if (!patientDetails) {
//         return null;
//     }

//     const rxnumber = details?.rxnumber;
//     const new_rxnumber = patientDetails?.new_rx?.rxnum;
//     const old_rxnumber = patientDetails?.old_rx?.rxnum;

//     // Check if rxnumber matches new_rxnumber
//     if (rxnumber === new_rxnumber && new_rxnumber) {
//         const endDate = new Date(patientDetails?.new_rx?.old_rxnumber_edate);
//         const currentDateObj = new Date(currentDate);
//         const duration_day = patientDetails?.new_rx?.duration_day;

//         if (old_rxnumber && endDate && endDate !== 'NaN Invalid Date, NaN' && patientDetails?.new_rx?.old_rxnumber_edate) {
//             if (endDate && Number(duration_day) >= 1 && endDate < currentDateObj) {
//                 return (
//                     <span>
//                         Prev. Rx #{createClickableRxLink(old_rxnumber, routeNames)} ended {duration_day} days ago {PharmacyformattedDate(patientDetails?.new_rx?.old_rxnumber_edate)}
//                     </span>
//                 );

//             } else {
//                 return (
//                     <span>
//                         Prev. Rx #{createClickableRxLink(old_rxnumber, routeNames)} ends on {PharmacyformattedDate(patientDetails?.new_rx?.old_rxnumber_edate)}
//                     </span>
//                 );
//             }
//         }
//     }

//     // Check if rxnumber matches old_rxnumber
//     if (rxnumber === old_rxnumber && old_rxnumber) {
//         const startDate = PharmacyformattedDate(patientDetails?.old_rx?.new_rxnumber_sdate);
//         if (new_rxnumber && startDate && startDate !== 'NaN Invalid Date, NaN' && patientDetails?.old_rx?.new_rxnumber_sdate) {
//             if (startDate) {
//                 return (
//                     <span>
//                         Next Rx #{createClickableRxLink(new_rxnumber, routeNames)} starts on {startDate}
//                     </span>
//                 );
//             } else {
//                 return (
//                     <span>
//                         Next Rx #{createClickableRxLink(new_rxnumber, routeNames)} not scheduled
//                     </span>
//                 );
//             }
//         }
//     }

//     return null;
// }


export const getRxStatusText = (details) => {
    // Check if details.din_analysis exists
    const patientDetails = details?.din_analysis_new;

    if (!patientDetails) {
        return null;
    }

    const new_rxnumber = patientDetails?.next_rx?.RXNUM;
    const old_rxnumber = patientDetails?.previous_rx?.RXNUM;
    return (
        <span className='flex items-center gap-3'>
            {old_rxnumber && <span className='flex items-center '><PrevIcons width={13} height={13} className='mr-1' />Prev #{createClickableRxLink(old_rxnumber, routeNames)}</span>}
            {new_rxnumber && <span className='flex items-center '>Next #{createClickableRxLink(new_rxnumber, routeNames)}<NextIcons width={13} height={13} className='ml-1' /></span>}
        </span>
    );
}


export const HandleWeeklyBilling = ({ selectedFQY, setSelectWeeklyDay, selectWeeklyDay, dispatch, patientDetails, prepStartDatePick }) => {

    // Use useRef to track if we've already processed this patient
    const hasProcessedRef = React.useRef(false);
    const processedRxNumberRef = React.useRef(null);
    const handledPrepStartDatePickRef = React.useRef(null);

    const defaultSchedule = patientDetails?.route_detail?.scheduled_days;
    const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

    const getDefaultDay = (scheduled_days) => {
        if (!scheduled_days) return '';
        const selectedDays = Object.keys(scheduled_days).filter(day => scheduled_days[day] === 1);
        // If only one day is selected, return it; else return ''
        return selectedDays?.length === 1 ? selectedDays[0] : '';
    };

    // Normalize with the app's existing Canada/Pacific date formatter, then
    // calculate the weekday from that date-only calendar value.
    const getWeekdayFromPrepStartDate = (dateValue) => {
        const appDate = formatDateToYYYYMMDD_CA(dateValue);
        if (!appDate) return '';

        const [year, month, day] = appDate.split('-').map(Number);
        if (![year, month, day].every(Number.isFinite)) return '';

        // Sunday = 0 through Saturday = 6. This works on the normalized
        // Canada/Pacific YYYY-MM-DD value and does not use browser/UTC time.
        const monthOffsets = [0, 3, 2, 5, 0, 3, 5, 1, 4, 6, 2, 4];
        const adjustedYear = month < 3 ? year - 1 : year;
        const weekdayIndex = (
            adjustedYear
            + Math.floor(adjustedYear / 4)
            - Math.floor(adjustedYear / 100)
            + Math.floor(adjustedYear / 400)
            + monthOffsets[month - 1]
            + day
        ) % 7;

        return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][weekdayIndex] || '';
    };

    useEffect(() => {
        // Only process if selectedFQY is 7
        if (Number(selectedFQY) !== 7) {
            return;
        }
        // Skip if we've already processed this rx number
        if (hasProcessedRef.current && processedRxNumberRef.current === patientDetails?.rxnumber) {
            return;
        }
        // First try to get day from ai_start_date
        const dayFromAiStart = patientDetails?.ai_start_day
        // Check if there's already a single day set in defaultSchedule
        const existingDefaultDay = getDefaultDay(defaultSchedule);

        // If ai_start_date exists and we have a valid day, use it
        if (dayFromAiStart) {
            // Only update API if there's no existing default day
            if (!existingDefaultDay) {
                setSelectWeeklyDay(dayFromAiStart);
                // Update the schedule in API
                const defaultDays = {};
                daysOfWeek.forEach(day => defaultDays[day] = (day === dayFromAiStart) ? 1 : 0);

                dispatch(HandleUpdateSchedule({
                    payload: {
                        rxnumber: patientDetails?.rxnumber,
                        days: defaultDays
                    }
                }));

                dispatch(handleUpdateIsScCarryDose({
                    requestType: 'update_is_sc_carry_dose',
                    payload: {
                        rx_number: patientDetails?.rxnumber,
                        is_sc_carry_dose: 0
                    },
                }));
            } else if (existingDefaultDay) {
                setSelectWeeklyDay(existingDefaultDay);
            }
        } else {
            // Fallback to existing logic
            if (existingDefaultDay) {
                setSelectWeeklyDay(existingDefaultDay);
            }
        }

        // Mark as processed for this rx number
        hasProcessedRef.current = true;
        processedRxNumberRef.current = patientDetails?.rxnumber;
    }, [patientDetails, selectedFQY]);

    useEffect(() => {
        if (Number(selectedFQY) !== 7) return;

        // Only a date the user picked moves the day. The date loaded from the API
        // must not, or a manually chosen day would be overwritten on reload.
        const rxnumber = patientDetails?.rxnumber;
        if (!prepStartDatePick?.seq || prepStartDatePick.rxnumber !== rxnumber) return;
        if (handledPrepStartDatePickRef.current === prepStartDatePick) return;
        if (!/^\d{4}-\d{2}-\d{2}$/.test(prepStartDatePick.date || '')) return;

        const weekday = getWeekdayFromPrepStartDate(prepStartDatePick.date);
        if (!weekday) return;

        handledPrepStartDatePickRef.current = prepStartDatePick;
        setSelectWeeklyDay(weekday);

        if (!rxnumber) return;

        const defaultDays = {};
        daysOfWeek.forEach(day => defaultDays[day] = day === weekday ? 1 : 0);

        dispatch(HandleUpdateSchedule({
            payload: {
                rxnumber,
                days: defaultDays
            }
        }));

        dispatch(handleUpdateIsScCarryDose({
            requestType: 'update_is_sc_carry_dose',
            payload: {
                rx_number: rxnumber,
                is_sc_carry_dose: 0
            },
        }));
    }, [prepStartDatePick, selectedFQY, patientDetails?.rxnumber, dispatch]);

    // Only render if selectedFQY is 7
    if (Number(selectedFQY) !== 7) {
        return null;
    }

    const handleSelectWeeklyDay = (e) => {
        const value = e.target.value; // value will be the day name, e.g., "Monday"
        setSelectWeeklyDay(value);
        const defaultDays = {};
        daysOfWeek.forEach(day => defaultDays[day] = (day === value) ? 1 : 0);

        dispatch(HandleUpdateSchedule({
            payload: {
                rxnumber: patientDetails?.rxnumber,
                days: defaultDays
            }
        }));

        dispatch(handleUpdateIsScCarryDose({
            requestType: 'update_is_sc_carry_dose',
            payload: {
                rx_number: patientDetails?.rxnumber,
                is_sc_carry_dose: 0
            },
        }))
    }

    return (
        <div>
            <p className="text-[11px] mb-1 text-gray-500 leading-[0.7em]">Select Day</p>
            <select
                value={selectWeeklyDay}
                onChange={(e) => handleSelectWeeklyDay(e)}
                className="w-[170px] px-2 h-[42px] border border-gray-300 rounded focus:outline-none focus:ring-0 focus:ring-transparent">
                <option value="" disabled>Select</option>
                {[{ label: 'Sunday', value: 'Sunday' },
                { label: 'Monday', value: 'Monday' },
                { label: 'Tuesday', value: 'Tuesday' },
                { label: 'Wednesday', value: 'Wednesday' },
                { label: 'Thursday', value: 'Thursday' },
                { label: 'Friday', value: 'Friday' },
                { label: 'Saturday', value: 'Saturday' }].map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
            </select>
        </div>
    )
}

export const calculateDurationDays = ({
    // formData,
    patientDetails,
    setFormData,
    setDurationInput,
    isManuallyDurationEditing,
    // dispatch,
    AIStartDate,
    durationInput = ''
}) => {

    // const { start_date } = formData;

    const start_date = AIStartDate
    const duration = durationInput ? Number(durationInput) : calculateDaysSupply(patientDetails);

    const hasDays = duration > 0

    // ✅ Phase 2: When start_date changes later, recalculate stop_date
    // Pass the date string directly to getStopDate to avoid timezone issues
    if (start_date && hasDays) {
        if (duration > 0) {
            const stopDate = getStopDate(start_date, duration);
            setFormData(prevData => ({
                ...prevData,
                stop_date: stopDate
            }));

            if (hasDays && start_date && stopDate) {
                setDurationInput(duration.toString())
            }

            // const ai_start_date = patientDetails?.ai_start_date;
            // const ai_stop_date = patientDetails?.ai_end_date; // Changed to a valid date

            // if (ai_start_date && ai_stop_date && ai_stop_date !== stopDate) {
            //     const payload = {
            //         rx_number: patientDetails?.rxnumber,
            //         ai_start_date: start_date,
            //         ai_stop_date: stopDate,
            //     }
            //     dispatch(handleUpdateAIDates({
            //         requestType: 'update_aidates',
            //         payload
            //     }))
            // }
        }
    } else {
        // If missing data, clear fields
        setFormData(prevData => ({
            ...prevData,
            days: '',
            stop_date: ''
        }));
        if (!isManuallyDurationEditing.current) {
            setDurationInput('');
        }
    }
}

export const calculateDaysSupply = (details) => {
    const refills_authorized = Number(details?.prescription_detail?.refills_authorized);
    const days_supply = Number(details?.prescription_detail?.day_supply);
    const duration = (refills_authorized > 0 ? ((refills_authorized + 1) * days_supply) : days_supply);
    return duration;
}

// Calculate scheduled days within a date range for fentanyl
// export const calculateFentanylScheduleInfo = (patientDetails,
//     selectedDaysArray,
//     durationInput,
//     formData,
//     fentanyl_Qty,
//     billingFrequency,
//     selectedFQY) => {

//     // Only compute for fentanyl and valid duration
//     if (!checkIsFentanyl(patientDetails) || !durationInput) {
//         return null;
//     }

//     // Normalize selected days
//     const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
//     const scheduledDays = {};
//     dayNames.forEach(day => {
//         scheduledDays[day] = selectedDaysArray.includes(day) ? 1 : 0;
//     });
//     const selectedDays = Object.entries(scheduledDays)
//         .filter(([, isSelected]) => isSelected === 1)
//         .map(([day]) => day);

//     // Duration in calendar days (inclusive window length)
//     const totalDays = parseInt(durationInput) || 0;
//     if (totalDays === 0) return null;

//     // Parse YYYY-MM-DD as UTC dates to avoid local/DST off-by-one
//     const parseUtcYmd = (value) => {
//         if (!value || value === '0000-00-00') return null;
//         const [yy, mm, dd] = String(value).split('-').map(Number);
//         if (!yy || !mm || !dd) return null;
//         return new Date(Date.UTC(yy, mm - 1, dd));
//     };
//     const startDate = parseUtcYmd(formData?.start_date);
//     const endDate = parseUtcYmd(formData?.stop_date);
//     if (!startDate || !endDate) return null;

//     // Count dispense days according to billing frequency
//     let scheduledDayCount = 0;

//     if (billingFrequency === 1 || billingFrequency === 28 || selectedFQY === 'changing' || selectedFQY === 'custom') {
//         scheduledDayCount = 0;
//     } else if (billingFrequency === 2) {
//         // Every other day: ceil(totalDays/2) starting from start date
//         scheduledDayCount = Math.ceil(totalDays / 2);
//     } else if (billingFrequency === 7) {
//         // Weekly: only complete weeks
//         scheduledDayCount = Math.floor(totalDays / 7);
//     } else if (selectedDays?.length > 0) {
//         // Specific weekdays: iterate using UTC, but resolve weekday in Canada/Pacific
//         const tz = 'Canada/Pacific';
//         const fmt = new Intl.DateTimeFormat('en-US', { weekday: 'long', timeZone: tz });
//         const cursor = new Date(startDate.getTime());
//         while (cursor <= endDate) {
//             const dow = fmt.format(cursor);
//             if (selectedDays.includes(dow)) {
//                 scheduledDayCount++;
//             }
//             cursor.setUTCDate(cursor.getUTCDate() + 1);
//         }
//     } else {
//         scheduledDayCount = 0;
//     }

//     const fentanylQty = Number(fentanyl_Qty || 0);
//     const dispenseQuantity = scheduledDayCount * fentanylQty;
//     const scheduleText = scheduledDayCount > 0 && `Total ${scheduledDayCount} days dispense. Calculated Auth ${dispenseQuantity}.`;

//     return {
//         scheduleText,
//         scheduledDayCount,
//         totalDays,
//         fentanylQty,
//         dispenseQuantity
//     };
// };




export const calculateFentanylScheduleInfo = (
    patientDetails,
    selectedDaysArray,
    durationInput,
    formData,
    fentanyl_Qty,
    billingFrequency,
    selectedFQY
) => {
    // ✅ Step 1: Validate that this is a fentanyl prescription and duration is valid
    if (!checkIsFentanyl(patientDetails) || !durationInput) {
        return null;
    }

    // ✅ Step 2: Normalize selected weekdays
    const allDayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const scheduledDaysMap = {};
    allDayNames.forEach(day => {
        scheduledDaysMap[day] = selectedDaysArray.includes(day) ? 1 : 0;
    });
    const selectedDays = Object.entries(scheduledDaysMap)
        .filter(([, v]) => v === 1)
        .map(([k]) => k);

    // ✅ Step 3: Parse duration (still used for billing frequency math)
    const totalDays = parseInt(durationInput, 10) || 0;
    if (totalDays === 0) return null;

    // ✅ Step 4: DST-safe date parser → sets the time to NOON UTC
    // This ensures the same calendar day is used in Pacific time,
    // preventing weekday shifts around daylight savings (DST).
    const parseDateNoonUTC = (value) => {
        if (!value || value === '0000-00-00') return null;
        const [yy, mm, dd] = String(value).split('-').map(Number);
        if (!yy || !mm || !dd) return null;
        // 12:00 UTC = 04:00 or 05:00 Pacific, so it stays the same local date
        return new Date(Date.UTC(yy, mm - 1, dd, 12, 0, 0));
    };

    const startDate = parseDateNoonUTC(formData?.start_date);
    const endDate = parseDateNoonUTC(formData?.stop_date);
    if (!startDate || !endDate) return null;

    // ✅ Step 5: Initialize counter
    let scheduledDayCount = 0;

    // ✅ Step 6: Apply billing frequency logic
    if (
        billingFrequency === 1 ||
        billingFrequency === 28 ||
        selectedFQY === 'changing' ||
        selectedFQY === 'custom'
    ) {
        scheduledDayCount = 0;
    } else if (billingFrequency === 2) {
        // Every other day
        scheduledDayCount = Math.ceil(totalDays / 2);
    } else if ([7, 14, 21].includes(billingFrequency)) {
        // Fixed interval dispense (only complete intervals)
        scheduledDayCount = Math.floor(totalDays / billingFrequency);
    } else if (selectedDays.length > 0) {
        // ✅ Step 7: Weekday-based iteration (M/W/F etc.)
        // Using UTC noon dates makes this safe even across DST
        const cursor = new Date(startDate.getTime());
        while (cursor.getTime() <= endDate.getTime()) {
            const dowIndex = cursor.getUTCDay(); // stable because we fixed date at noon UTC
            const dowName = allDayNames[dowIndex];
            if (selectedDays.includes(dowName)) {
                scheduledDayCount++;
            }
            // move forward by one calendar day
            cursor.setUTCDate(cursor.getUTCDate() + 1);
        }
    } else {
        scheduledDayCount = 0;
    }

    // ✅ Step 8: Compute quantity and build return text
    const fentanylQty = Number(fentanyl_Qty || 0);
    const dispenseQuantity = scheduledDayCount * fentanylQty;

    const scheduleText =
        scheduledDayCount > 0
            ? `Qty Authorized - ${dispenseQuantity} (Total Patch changed ${scheduledDayCount})`
            : '';

    // ✅ Step 9: Return final structured object
    return {
        scheduleText,
        scheduledDayCount,
        totalDays,
        fentanylQty,
        dispenseQuantity
    };
};




// Calculate scheduled days within a date range for fentanyl
// export const calculateFentanylScheduleInfo = (patientDetails,
//     selectedDaysArray,
//     durationInput,
//     formData,
//     fentanyl_Qty,
//     billingFrequency,
//     selectedFQY) => {

//     // const defaultFqy = Number(patientDetails?.billing_frequency);

//     if (!checkIsFentanyl(patientDetails) || !durationInput) {
//         return null;
//     }

//     // Transform selectedDaysArray to scheduledDays format for real-time updates
//     const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
//     const scheduledDays = {};
//     dayNames.forEach(day => {
//         scheduledDays[day] = selectedDaysArray.includes(day) ? 1 : 0;
//     });

//     // Get the selected days (where value === 1)
//     const selectedDays = Object.entries(scheduledDays)
//         .filter(([day, isSelected]) => isSelected === 1)
//         .map(([day]) => day);

//     // Use the existing calculated duration
//     const totalDays = parseInt(durationInput) || 0;

//     if (totalDays === 0) return null;

//     // Get start and end dates with proper validation
//     const getValidDate = (dateValue) => {
//         if (!dateValue || dateValue === '0000-00-00' || dateValue === '' || dateValue === null) {
//             return null;
//         }
//         const date = new Date(dateValue);
//         return isNaN(date.getTime()) ? null : date;
//     };

//     const startDate = getValidDate(formData?.start_date);
//     const endDate = getValidDate(formData?.stop_date);

//     // Check if we have valid dates
//     if (!startDate || !endDate) return null;

//     // Count scheduled days within the duration window
//     let scheduledDayCount = 0;
//     const currentDate = new Date(startDate);
//     let dayCounter = 0; // Track which day we're on (0, 1, 2, 3...)


//     if (billingFrequency === 1 || billingFrequency === 28 || selectedFQY === 'changing' || selectedFQY === 'custom') {
//         scheduledDayCount = 0
//     }
//     else {
//         while (currentDate <= endDate) {
//             const dayOfWeek = currentDate.toLocaleDateString('en-US', { weekday: 'long' });
//             // Check if billing frequency is 2 (every other day)
//             if (billingFrequency === 2) {
//                 // For every other day, only count if it's an even day (0, 2, 4, 6...)
//                 if (dayCounter % 2 === 0) {
//                     // If no specific days selected, count every other day regardless of day of week
//                     scheduledDayCount++;
//                 }
//             } else if (billingFrequency === 7) {
//                 // Reset scheduledDayCount for weekly calculation
//                 scheduledDayCount = 0;
//                 // Calculate only complete weeks (ignore remaining days)
//                 const completeWeeks = Math.floor(totalDays / 7);
//                 // If no specific days selected, count all days in complete weeks only
//                 scheduledDayCount = completeWeeks

//                 // For weekly billing, we need to calculate based on complete weeks
//                 // This will be handled after the loop, so we'll skip individual day counting here
//                 // and calculate it properly after the loop
//             } else if (selectedDays?.length > 0) {
//                 // For other billing frequencies, use existing logic
//                 if (selectedDays.includes(dayOfWeek)) {
//                     scheduledDayCount++;
//                 }
//             } else {
//                 scheduledDayCount = 0;
//             }

//             currentDate.setDate(currentDate.getDate() + 1);
//             dayCounter++;
//         }
//     }

//     // Get fentanyl quantity per day
//     const fentanylQty = fentanyl_Qty

//     // Calculate dispense quantity
//     const dispenseQuantity = scheduledDayCount * fentanylQty;
//     // Generate the schedule summary message
//     let scheduleText = '';
//     scheduleText = scheduledDayCount > 0 && `Total ${scheduledDayCount} days dispense. Calculated Auth ${dispenseQuantity}.`;

//     return {
//         scheduleText,
//         scheduledDayCount,
//         totalDays,
//         fentanylQty,
//         dispenseQuantity
//     };
// };


export const UpdateDilaudidVS = ({
    checkRxnumberDilaudidResponse,
    dispatch,
    rxNumber,
    patientDetails,
    selectedFQY
}) => {

    const { updatePacmedTimeResponse } = useAccountibilitySelector()
    const [updatedValues, setUpdatedValues] = useState({}); // Track updated pacmed values

    const pacmedTimeOrder = ['0800', '1200', '1800', '2100', '2200', '2300', '2350'];

    const pacmed_overwrite = checkRxnumberDilaudidResponse?.data?.pacmed_overwrite
    const is_dilaudid = checkRxnumberDilaudidResponse?.data?.is_dilaudid
    const total_dose = patientDetails?.route_detail?.daily_dose

    const handleUpdatePacmedTimeValue = () => {
        if (!pacmed_overwrite) return;

        const pacmedValues = {};
        let sumOfPacmed = 0;

        // Collect all pacmed values (including updated values)
        pacmedTimeOrder?.forEach(time => {
            // Get updated value if exists, otherwise use original value
            const updatedValue = updatedValues[time];
            const value = updatedValue !== undefined ? Number(updatedValue) : Number(pacmed_overwrite[time] || 0);
            pacmedValues[time] = value;
            sumOfPacmed += value;
        });

        // Validate against total dose
        if (sumOfPacmed !== Number(total_dose || 0)) {
            // showToast(`Sum of pacmed values (${sumOfPacmed}) does not match total dose (${total_dose})`, 'error');
            showToast(`Entered doses ${sumOfPacmed} exceed the total dose ${total_dose}`, 'error');
            return;
        }

        // If validation passes, dispatch the update
        const payload = {
            rx_number: rxNumber,
            pacmed: pacmedValues
        }

        dispatch(UpdatePacmedTime({
            fetchType: `updatePacmedTime-${rxNumber}`,
            payload
        })).then((res) => {
            if (res?.success) {
                setUpdatedValues({});
            }
        })
    };

    // Function to check if there are any updated values
    const hasUpdatedValues = () => {
        return Object.keys(updatedValues).length > 0;
    };

    return (<>
        {is_dilaudid && Number(selectedFQY) === 1 && (
            <div className='bg-white rounded-md border border-gray-300  relative overflow-auto mt-[12px]'>
                <table className="w-full table-fixed">
                    <thead>
                        <tr className='bg-[#E5E7EB] text-white uppercase relative'>
                            {pacmedTimeOrder?.map((time, idx) => (
                                <th key={time} className={`text-center px-2 py-1   text-sm font-semibold  text-black ${idx === 0 ? '' : 'border-l'} border-gray-300`}>
                                    {time}
                                    <p>{idx === 0 ? 'VS' : 'UFVS'}</p>
                                </th>
                            ))}
                            {/* Only show header if there are any updated values */}
                            {hasUpdatedValues() && (
                                <th className='text-center px-2 py-1  text-sm font-semibold text-black border-l border-gray-300'></th>
                            )}
                        </tr>
                    </thead>

                    <tbody>
                        <tr>
                            {pacmedTimeOrder?.map((time, idx) => (
                                <td
                                    key={time}
                                    className={`px-2 py-2 text-center align-middle border-gray-300 ${idx === 0 ? '' : 'border-l'} focus:outline-none`}
                                    contentEditable={true}
                                    suppressContentEditableWarning={true}
                                    onInput={(e) => {
                                        // Allow numbers and decimal point, but only one decimal point
                                        const inputValue = e.target.textContent;
                                        const decimalValue = inputValue.replace(/[^0-9.]/g, '');

                                        // Check if there's more than one decimal point
                                        const decimalCount = (decimalValue.match(/\./g) || []).length;
                                        const cleanValue = decimalCount > 1
                                            ? decimalValue.replace(/\./g, (match, index) =>
                                                index === decimalValue.indexOf('.') ? '.' : ''
                                            )
                                            : decimalValue;

                                        // Only update content if non-numeric chars were removed or decimal points were fixed
                                        if (inputValue !== cleanValue) {
                                            // Save current cursor position before updating
                                            const selection = window.getSelection();
                                            const range = selection.getRangeAt(0);
                                            const cursorPosition = range.startOffset;

                                            // Update cell content with clean value
                                            e.target.textContent = cleanValue;

                                            // Restore cursor to its original position
                                            if (cleanValue.length > 0) {
                                                const newRange = document.createRange();
                                                const textNode = e.target.firstChild || e.target;
                                                const newPosition = Math.min(cursorPosition, cleanValue.length);
                                                newRange.setStart(textNode, newPosition);
                                                newRange.setEnd(textNode, newPosition);
                                                selection.removeAllRanges();
                                                selection.addRange(newRange);
                                            }
                                        }

                                        // Track the updated value
                                        setUpdatedValues(prev => ({
                                            ...prev,
                                            [time]: cleanValue
                                        }));
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            e.target.blur();
                                        }
                                    }}
                                >
                                    {pacmed_overwrite?.[time] ?? ''}
                                </td>
                            ))}


                            {hasUpdatedValues() && (
                                <td className='px-2 py-2 text-center align-middle border-l border-gray-300'>
                                    <button
                                        onClick={handleUpdatePacmedTimeValue}
                                        className='bg-blue-700 text-white text-xs px-2 py-1 rounded transition-colors duration-200'>
                                        {/* <Loader color='white' width='16' /> */}
                                        {updatePacmedTimeResponse?.loading && updatePacmedTimeResponse?.fetchType === `updatePacmedTime-${rxNumber}` ? <Loader color='white' width='16' /> : 'UPDATE'}
                                    </button>
                                </td>
                            )}
                        </tr>
                    </tbody>
                </table>
            </div>
        )}
    </>)
}

export const FrequencyOptions = ({ is_dilaudid, pacmed_overwrite, safeSupply }) => {

    const options = {
        "OD": "OD",
        "BID": "BID",
        "TID": "TID",
        "QID": "QID",
        ...(((is_dilaudid && pacmed_overwrite) || safeSupply) ? {
            "5x": "5x",
            "6x": "6x",
            "7x": "7x"
        } : {}),
    }

    // {Object.entries(patientDetails?.frequency_array || {}).map(([key, value]) => (
    //     <option value={value} key={key}>{value}</option>
    // ))}
    return options
}

/** IV code: only capital letters A-Z, max 4 chars. */
const sanitizeIvCode = (value) => {
    if (value == null || value === '') return ''
    return String(value).toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4)
}

// Intervention/IV codes are provided by API (`patientDetails.intervention_code_values`).

const IvCodeDropdown = ({ value, onChange, options, disabled, placeholder = 'Code', allowClear = true }) => {
    const [open, setOpen] = useState(false)
    const rootRef = useRef(null)
    const buttonRef = useRef(null)
    const [menuPos, setMenuPos] = useState(null)

    useEffect(() => {
        if (!open) return
        const onMouseDown = (e) => {
            const el = rootRef.current
            if (!el) return
            if (el.contains(e.target)) return
            setOpen(false)
        }
        document.addEventListener('mousedown', onMouseDown)
        return () => document.removeEventListener('mousedown', onMouseDown)
    }, [open])

    useEffect(() => {
        if (!open) {
            setMenuPos(null)
            return
        }
        const width = 88
        const estimatedMenuHeight = (allowClear ? 34 : 0) + (options?.length || 0) * 28

        const updatePos = () => {
            const btn = buttonRef.current
            if (!btn) return
            const rect = btn.getBoundingClientRect()

            const viewportH = window.innerHeight || 0
            const spaceBelow = viewportH - rect.bottom
            const openUp = spaceBelow < estimatedMenuHeight && rect.top > estimatedMenuHeight

            const top = openUp ? Math.max(8, rect.top - 4 - estimatedMenuHeight) : rect.bottom + 4
            const left = Math.max(8, rect.right - width)

            setMenuPos({
                top,
                left,
                width,
            })
        }

        updatePos()

        // Keep anchored while scrolling (capture true catches scroll from any container)
        window.addEventListener('scroll', updatePos, true)
        window.addEventListener('resize', updatePos)
        return () => {
            window.removeEventListener('scroll', updatePos, true)
            window.removeEventListener('resize', updatePos)
        }
    }, [allowClear, open, options?.length])

    const display = value || placeholder
    const isPlaceholder = !value

    return (
        <div ref={rootRef} className="relative">
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen(v => !v)}
                ref={buttonRef}
                className={`h-[42px] w-[64px] min-w-[64px] px-1 text-[10px] border-l border-l-gray-200 bg-gray-50 text-center flex items-center justify-center gap-0.5 select-none ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-100'} focus:outline-none`}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className={`${isPlaceholder ? 'text-gray-400' : 'text-gray-700'} leading-none`}>
                    {display}
                </span>
                <svg width="10" height="10" viewBox="0 0 20 20" className="text-gray-500 shrink-0">
                    <path fill="currentColor" d="M5.5 7.5l4.5 5 4.5-5H5.5z" />
                </svg>
            </button>

            {open && !disabled && (
                menuPos &&
                createPortal(
                    <div
                        role="listbox"
                        style={{
                            position: 'fixed',
                            top: menuPos.top,
                            left: menuPos.left,
                            width: menuPos.width,
                            zIndex: 9999,
                        }}
                        onMouseDown={(e) => {
                            // Prevent the document mousedown handler from closing the menu
                            // before option buttons receive their click.
                            e.stopPropagation()
                        }}
                        className="bg-white border border-gray-300 rounded shadow-lg overflow-hidden max-h-[180px] overflow-y-auto"
                    >
                        {allowClear && (
                            <button
                                type="button"
                                className="w-full px-2 py-1 text-left text-[11px] hover:bg-gray-100 text-gray-500"
                                onClick={() => {
                                    onChange?.(null)
                                    setOpen(false)
                                }}
                            >
                                Clear
                            </button>
                        )}
                        {options.map((opt) => (
                            <button
                                key={opt}
                                type="button"
                                className={`w-full px-2 py-1 text-left text-[11px] hover:bg-blue-600 hover:text-white ${opt === value ? 'bg-blue-600 text-white' : 'text-gray-700'}`}
                                onClick={() => {
                                    onChange?.(opt)
                                    setOpen(false)
                                }}
                            >
                                {opt}
                            </button>
                        ))}
                    </div>,
                    document.body
                )
            )}
        </div>
    )
}

//not responsive 
export const renderFrequencyRows1 = ({ isDilaudid,
    frequencyRows,
    setFrequencyRows,
    patientDetails,
    is_dilaudid,
    pacmed_overwrite,
    doseErrors,
    removeDays,
    onUpdateIvCode
}) => {

    const safeSupply = patientDetails?.rxsafesupply === 'Y'
    // const options = isDilaudid
    //     ? Object.entries(FrequencyOptions({ is_dilaudid, pacmed_overwrite, safeSupply }))
    //     : [['OD', 'OD']]
    const options = Object.entries(FrequencyOptions({ is_dilaudid, pacmed_overwrite, safeSupply }))

    const qtyLabel = patientDetails?.is_fentanyl === 1 ? 'PATCHES' : 'QTY'

    return frequencyRows?.map((row, rowIndex) => (
        <div key={rowIndex} className={`w-full grid grid-cols-12 ${isDilaudid ? 'gap-2 mb-1' : 'gap-1 mt-2 mb-1'}`}>
            <div className={`${isDilaudid ? 'col-span-9 flex flex-wrap xl:flex-nowrap gap-3' : 'col-span-3 flex flex-wrap xl:flex-nowrap gap-2'}`}>
                <div className={`flex flex-col ${isDilaudid ? 'w-[90px]' : 'w-[90px]'}`}>
                    <label htmlFor="frequency" className="text-gray-500 text-[11px]">
                        FQY
                    </label>
                    <select
                        id={`frequency-${rowIndex}`}
                        value={row.frequency || ''}
                        // disabled={!!row.cycle_no}
                        onChange={(e) => {
                            const selectedFreq = e.target.value
                            const previousFreq = row.frequency
                            const doseCount = {
                                'OD': 1,
                                'BID': 2,
                                'TID': 3,
                                'QID': 4,
                                '5x': 5,
                                '6x': 6,
                                '7x': 7
                            }[selectedFreq] || 0

                            setFrequencyRows(prevRows => {
                                const newRows = [...prevRows]

                                // Store current data for the previous frequency
                                const currentDoseRows = newRows[rowIndex].dose_rows || []
                                if (previousFreq && (currentDoseRows.some(dr => dr.dose !== '') || row.days)) {
                                    newRows[rowIndex] = {
                                        ...newRows[rowIndex],
                                        [`stored_${previousFreq}`]: {
                                            dose_rows: currentDoseRows.map(dr => ({ ...dr })),
                                            days: row.days || ''
                                        }
                                    }
                                }
                                const storedData = newRows[rowIndex][`stored_${selectedFreq}`]
                                const baseRows = storedData?.dose_rows?.length
                                    ? storedData.dose_rows.map(dr => ({ id: dr.id, dose: dr.dose ?? '', iv_code: dr.iv_code ?? '' }))
                                    : []
                                const newDoseRows = baseRows.length >= doseCount
                                    ? baseRows.slice(0, doseCount)
                                    : [...baseRows, ...Array.from({ length: doseCount - baseRows.length }, () => ({ ...DEFAULT_DOSE_ROW() }))]
                                newRows[rowIndex] = {
                                    ...newRows[rowIndex],
                                    frequency: selectedFreq,
                                    dose_rows: newDoseRows,
                                    days: storedData ? storedData.days : ''
                                }
                                return newRows
                            })
                        }}
                        className={`${isDilaudid ? 'w-[90px]' : 'w-[90px]'} px-3 h-[42px] py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-transparent ${row.cycle_no ? 'bg-white cursor-allowed' : ''}`}
                    >
                        <option value="" disabled>FQY</option>
                        {options.map(([key, value]) => (
                            <option value={value} key={key}>{value}</option>
                        ))}
                    </select>
                </div>

                {(row.dose_rows || []).map((dr, index) => {
                    const dose = dr.dose ?? ''
                    const code = dr.iv_code ?? ''
                    return (
                        <div key={dr.id ?? index} className={`flex flex-col ${safeSupply ? 'w-[90px] min-w-[90px]' : 'min-w-[48px]'}`}>
                            <label htmlFor="dose" className="text-gray-500 text-[11px]">{isDilaudid ? getDosePlaceholder(index + 1) : qtyLabel}</label>
                            <div className={`flex overflow-visible border rounded-md h-[42px] ${doseErrors[index] ? 'border-red-500' : 'border-gray-300'}`}>
                                <input
                                    type="text"
                                    value={dose}
                                    onChange={(e) => {
                                        const value = e.target.value
                                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                            setFrequencyRows(prevRows => {
                                                const newRows = [...prevRows]
                                                const newDoseRows = (newRows[rowIndex].dose_rows || []).map((r, i) => i === index ? { ...r, dose: value } : { ...r })
                                                newRows[rowIndex] = { ...newRows[rowIndex], dose_rows: newDoseRows }
                                                if (row.frequency) {
                                                    newRows[rowIndex] = {
                                                        ...newRows[rowIndex],
                                                        [`stored_${row.frequency}`]: {
                                                            dose_rows: newDoseRows.map(r => ({ ...r })),
                                                            days: row.days || ''
                                                        }
                                                    }
                                                }
                                                return newRows
                                            })
                                        }
                                    }}
                                    className={`${safeSupply ? 'flex-1 min-w-0 w-full' : 'w-full'} px-2 py-1 text-gray-700 focus:ring-2 focus:ring-transparent focus:outline-none border-none bg-white`}
                                />
                                {safeSupply && (
                                    <input
                                        id={`dose-code-${rowIndex}-${index}`}
                                        type="text"
                                        placeholder="CODE"
                                        autoComplete="off"
                                        value={code}
                                        onChange={(e) => {
                                            const value = sanitizeIvCode(e.target.value)
                                            if (dr.id != null && typeof onUpdateIvCode === 'function') {
                                                onUpdateIvCode({ id: dr.id, iv_code: value || null })
                                            }
                                            setFrequencyRows(prevRows => {
                                                const newRows = [...prevRows]
                                                const newDoseRows = (newRows[rowIndex].dose_rows || []).map((r, i) => i === index ? { ...r, iv_code: value || null } : { ...r })
                                                newRows[rowIndex] = { ...newRows[rowIndex], dose_rows: newDoseRows }
                                                if (row.frequency) {
                                                    newRows[rowIndex] = {
                                                        ...newRows[rowIndex],
                                                        [`stored_${row.frequency}`]: {
                                                            dose_rows: newDoseRows.map(r => ({ ...r })),
                                                            days: row.days || ''
                                                        }
                                                    }
                                                }
                                                return newRows
                                            })
                                        }}
                                        className="px-1 py-1 text-xs text-gray-700 text-center border-l border-l-gray-200 bg-gray-50 focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-gray-400 focus:bg-gray-100 border-none w-[36px] min-w-[36px] placeholder:text-gray-400 placeholder:text-[10px]"
                                    />
                                )}
                            </div>
                            {doseErrors[index] && (
                                <span className="text-red-700 font-semibold text-xs mt-1">{doseErrors[index]}</span>
                            )}
                        </div>
                    )
                })}
            </div>

            {removeDays ? null : row.frequency && (
                <div className={`${isDilaudid ? 'col-span-1 flex gap-2' : 'col-span-2'}`}>
                    <div className="flex flex-col w-full">
                        <label htmlFor="days" className="text-gray-500 text-[11px] uppercase">Days</label>
                        <input
                            type="text"
                            value={row.days || ''}
                            onChange={(e) => setFrequencyRows(prevRows => {
                                const newRows = [...prevRows]
                                newRows[rowIndex] = { ...newRows[rowIndex], days: e.target.value }
                                if (row.frequency) {
                                    const dose_rows = (newRows[rowIndex].dose_rows || []).map(r => ({ ...r }))
                                    newRows[rowIndex] = {
                                        ...newRows[rowIndex],
                                        [`stored_${row.frequency}`]: {
                                            dose_rows,
                                            days: e.target.value
                                        }
                                    }
                                }
                                return newRows
                            })}
                            className={`w-full px-2 py-2 border border-gray-300 fs-12 h-[42px] rounded focus:outline-none focus:ring-2 focus:ring-transparent`}
                        />
                    </div>
                </div>
            )}

            {removeDays ? null : row.frequency && (
                <div className={`${isDilaudid ? 'col-span-2 flex gap-2 mt-[16.6px]' : 'col-span-1 flex gap-1 mt-[16.6px]'}`}>
                    {rowIndex === frequencyRows.length - 1 && (
                        <button onClick={() => handleAddRow(setFrequencyRows, frequencyRows)}
                            className={`bg-blue-600 text-white py-1 rounded h-[42px] ${isDilaudid ? 'text-lg w-[60px] px-4' : 'text-lg flex justify-center items-center w-[40px] px-4'} hover:bg-blue-500`}>
                            +
                        </button>
                    )}
                    {rowIndex > 0 && (
                        <button onClick={() => handleRemoveRow(rowIndex, setFrequencyRows, frequencyRows, row.cycle_no, patientDetails?.rxdata_id)}
                            className={`bg-red-500 text-white py-1 rounded h-[42px] hover:bg-red-400 ${isDilaudid ? 'text-lg w-[60px] px-4' : 'text-lg flex justify-center items-center w-[40px] px-4'}`}>
                            -
                        </button>
                    )}
                </div>
            )}

        </div>
    ))
}


// export const renderFrequencyRows = ({ isDilaudid,
//     frequencyRows,
//     setFrequencyRows,
//     patientDetails,
//     is_dilaudid,
//     pacmed_overwrite,
//     doseErrors,
//     removeDays
// }) => {

//     const options = isDilaudid
//         ? Object.entries(FrequencyOptions({ is_dilaudid, pacmed_overwrite }))
//         : [['OD', 'OD']]

//     const qtyLabel = patientDetails?.is_fentanyl === 1 ? 'PATCHES' : 'QTY'

//     return frequencyRows?.map((row, rowIndex) => (
//         <div key={rowIndex} className={`w-full flex  ${isDilaudid ? 'gap-1 mb-1' : 'gap-1 mt-2 mb-1'}`}>
//             <div className={`${isDilaudid ? ' flex gap-1' : ' flex gap-1'}`}>
//                 <div className={`flex flex-col ${isDilaudid ? 'w-[81px]' : 'w-[81px]'}`}>
//                     <label htmlFor="frequency" className="text-gray-500 text-[9px]">
//                         FQY
//                     </label>
//                     <select
//                         id={`frequency-${rowIndex}`}
//                         value={row.frequency || ''}
//                         // disabled={!!row.cycle_no}
//                         onChange={(e) => {
//                             const selectedFreq = e.target.value
//                             const previousFreq = row.frequency
//                             const doseCount = {
//                                 'OD': 1,
//                                 'BID': 2,
//                                 'TID': 3,
//                                 'QID': 4,
//                                 '5x': 5,
//                                 '6x': 6,
//                                 '7x': 7
//                             }[selectedFreq] || 0

//                             setFrequencyRows(prevRows => {
//                                 const newRows = [...prevRows]

//                                 // Store current data for the previous frequency
//                                 if (previousFreq && (row.doses?.some(dose => dose !== '') || row.days)) {
//                                     newRows[rowIndex] = {
//                                         ...newRows[rowIndex],
//                                         [`stored_${previousFreq}`]: {
//                                             doses: row.doses || [],
//                                             days: row.days || ''
//                                         }
//                                     }
//                                 }

//                                 // Check if we have stored data for the new frequency
//                                 const storedData = newRows[rowIndex][`stored_${selectedFreq}`]

//                                 // Update current row with new frequency and stored data
//                                 newRows[rowIndex] = {
//                                     ...newRows[rowIndex],
//                                     frequency: selectedFreq,
//                                     doses: storedData ? storedData.doses : new Array(doseCount).fill(''),
//                                     days: storedData ? storedData.days : ''
//                                 }

//                                 return newRows
//                             })
//                         }}
//                         className={`${isDilaudid ? 'w-[81px]' : 'w-[81px]'} px-3 h-[42px] py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-transparent ${row.cycle_no ? 'bg-white cursor-allowed' : ''}`}
//                     >
//                         <option value="" disabled>FQY</option>
//                         {options.map(([key, value]) => (
//                             <option value={value} key={key}>{value}</option>
//                         ))}
//                     </select>
//                 </div>

//                 {row.doses?.map((dose, index) => (
//                     <React.Fragment key={index}>
//                         <div className="flex flex-col max-w-[48px]">
//                             <label htmlFor="dose" className="text-gray-500 text-[9px]">{isDilaudid ? getDosePlaceholder(index + 1) : qtyLabel}</label>
//                             <input
//                                 type="text"
//                                 value={dose || ''}
//                                 onChange={(e) => {
//                                     const value = e.target.value
//                                     if (value === '' || /^\d*\.?\d*$/.test(value)) {
//                                         setFrequencyRows(prevRows => {
//                                             const newRows = [...prevRows]
//                                             const newDoses = [...(newRows[rowIndex].doses || [])]
//                                             newDoses[index] = value
//                                             newRows[rowIndex] = { ...newRows[rowIndex], doses: newDoses }

//                                             // Store the updated doses for the current frequency
//                                             if (row.frequency) {
//                                                 newRows[rowIndex] = {
//                                                     ...newRows[rowIndex],
//                                                     [`stored_${row.frequency}`]: {
//                                                         doses: newDoses,
//                                                         days: row.days || ''
//                                                     }
//                                                 }
//                                             }

//                                             return newRows
//                                         })
//                                     }
//                                 }}
//                                 className={`max-w-[45px] px-2 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-transparent ${doseErrors[index] ? 'border-red-500' : 'border-gray-300'}`}
//                             />
//                             {doseErrors[index] && (
//                                 <span className="text-red-700 font-semibold text-xs mt-1">{doseErrors[index]}</span>
//                             )}
//                         </div>
//                     </React.Fragment>
//                 ))}
//             </div>

//             {removeDays ? null : row.frequency && (
//                 <div className={`${isDilaudid ? ' flex gap-1' : ''}`}>
//                     <div className="flex flex-col max-w-[48px]">
//                         <label htmlFor="days" className="text-gray-500 text-[9px] uppercase">Days</label>
//                         <input
//                             type="text"
//                             value={row.days || ''}
//                             onChange={(e) => setFrequencyRows(prevRows => {
//                                 const newRows = [...prevRows]
//                                 newRows[rowIndex] = { ...newRows[rowIndex], days: e.target.value }

//                                 // Store the updated days for the current frequency
//                                 if (row.frequency) {
//                                     newRows[rowIndex] = {
//                                         ...newRows[rowIndex],
//                                         [`stored_${row.frequency}`]: {
//                                             doses: row.doses || [],
//                                             days: e.target.value
//                                         }
//                                     }
//                                 }

//                                 return newRows
//                             })}
//                             className={`max-w-[45px] px-2 py-2 border border-gray-300 fs-12 h-[42px] rounded focus:outline-none focus:ring-2 focus:ring-transparent`}
//                         />
//                     </div>
//                 </div>
//             )}

//             {removeDays ? null : row.frequency && (
//                 <div className={`${isDilaudid ? ' flex gap-1 mt-[12.6px]' : ' flex gap-1 mt-[12.6px]'}`}>

//                     {rowIndex === frequencyRows.length - 1 && (
//                         <button onClick={() => handleAddRow(setFrequencyRows, frequencyRows)}
//                             className={`bg-blue-600 text-white py-1 rounded h-[42px] flex justify-center items-center text-lg w-[40px] px-2  hover:bg-blue-500`}>
//                             +
//                         </button>
//                     )}
//                     {rowIndex > 0 && (
//                         <button onClick={() => handleRemoveRow(rowIndex, setFrequencyRows, frequencyRows, row.cycle_no, patientDetails?.rxdata_id)}
//                             className={`bg-red-500 text-white py-1 rounded h-[42px] hover:bg-red-400 flex justify-center items-center text-lg  w-[40px] px-2 `}>
//                             -
//                         </button>
//                     )}
//                 </div>
//             )}
//         </div>
//     ))
// }


//responsive running
export const renderFrequencyRows = ({ isDilaudid,
    frequencyRows,
    setFrequencyRows,
    patientDetails,
    is_dilaudid,
    pacmed_overwrite,
    doseErrors,
    removeDays,
    onUpdateIvCode,
    onUpdateDoseCode,
    hideIvCodeUpdatedBy = false,
    // restrictFrequencyToOd = false
}) => {

    const safeSupply = patientDetails?.rxsafesupply === 'Y'
    const ivCodeOptions = Array.isArray(patientDetails?.intervention_code_values)
        ? patientDetails.intervention_code_values
        : ['VS', 'SA', 'VSUF', 'UFSA']
    const doseCodeOptions = ['Witness', 'Carry']

    // const options = restrictFrequencyToOd
    //     ? [['OD', 'OD']]
    //     : Object.entries(FrequencyOptions({ is_dilaudid, pacmed_overwrite, safeSupply }))
    const options = Object.entries(FrequencyOptions({ is_dilaudid, pacmed_overwrite, safeSupply }))

    const qtyLabel = patientDetails?.is_fentanyl === 1 ? 'PATCHES' : 'QTY'

    return frequencyRows?.map((row, rowIndex) => (
        <div key={rowIndex} className="w-full mb-1 flex flex-wrap items-end gap-1 2xl:gap-2">
            <div className="flex flex-wrap items-end gap-1 2xl:gap-2">
                <div className={`flex flex-col 2xl:w-[86px] w-[81px]`}>
                    <label htmlFor="frequency" className="text-gray-500 2xl:text-[11px] text-[9px]">
                        FQY
                    </label>
                    <select
                        id={`frequency-${rowIndex}`}
                        value={row.frequency || ''}
                        // disabled={!!row.cycle_no}
                        onChange={(e) => {
                            const selectedFreq = e.target.value
                            const previousFreq = row.frequency
                            const doseCount = {
                                'OD': 1,
                                'BID': 2,
                                'TID': 3,
                                'QID': 4,
                                '5x': 5,
                                '6x': 6,
                                '7x': 7
                            }[selectedFreq] || 0

                            setFrequencyRows(prevRows => {
                                const newRows = [...prevRows]
                                const currentDoseRows = newRows[rowIndex].dose_rows || []

                                // Store current data for the previous frequency
                                if (previousFreq && (currentDoseRows.some(dr => dr.dose !== '') || row.days)) {
                                    newRows[rowIndex] = {
                                        ...newRows[rowIndex],
                                        [`stored_${previousFreq}`]: {
                                            dose_rows: currentDoseRows.map(dr => ({ ...dr })),
                                            days: row.days || ''
                                        }
                                    }
                                }

                                // Check if we have stored data for the new frequency
                                const storedData = newRows[rowIndex][`stored_${selectedFreq}`]
                                const baseRows = storedData?.dose_rows?.length
                                    ? storedData.dose_rows.map(dr => ({ id: dr.id, dose: dr.dose ?? '', iv_code: dr.iv_code ?? '', dose_code: dr.dose_code ?? '', dose_code_updated_by: dr.dose_code_updated_by ?? null, send_to_winrx: dr.send_to_winrx === true }))
                                    : []
                                const resizedDoseRows = baseRows.length >= doseCount
                                    ? baseRows.slice(0, doseCount)
                                    : [...baseRows, ...Array.from({ length: doseCount - baseRows.length }, () => ({ ...DEFAULT_DOSE_ROW() }))]
                                const newDoseRows = normalizeWinRxDoseSelection(resizedDoseRows)

                                newRows[rowIndex] = {
                                    ...newRows[rowIndex],
                                    frequency: selectedFreq,
                                    dose_rows: newDoseRows,
                                    days: storedData ? storedData.days : ''
                                }

                                return newRows
                            })
                        }}
                        className={`2xl:w-[86px] w-[81px] px-3 h-[42px] py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-transparent ${row.cycle_no ? 'bg-white cursor-allowed' : ''}`}
                    >
                        <option value="" disabled>FQY</option>
                        {options.map(([key, value]) => (
                            <option value={value} key={key}>{value}</option>
                        ))}
                    </select>
                </div>

                {(row.dose_rows || []).map((dr, index) => {
                    const dose = dr.dose ?? ''
                    const code = dr.iv_code ?? ''
                    const doseCode = dr.dose_code ?? ''
                    const needsDoseCode = !safeSupply && row.frequency && row.frequency !== 'OD'
                    const showWinRxSelection = !removeDays && row.frequency !== 'OD'
                    const hasOtherWinRxSelection = showWinRxSelection && (row.dose_rows || []).some((doseRow, doseIndex) => doseIndex !== index && doseRow?.send_to_winrx === true)
                    return (
                        <div key={dr.id ?? index} className={`flex flex-col shrink-0 ${safeSupply ? 'w-[120px] min-w-[120px] max-w-[120px]' : needsDoseCode ? 'w-[128px] min-w-[128px] max-w-[128px]' : 'w-[100px] min-w-[100px] max-w-[100px]'}`}>
                            <div className="flex items-center gap-1">
                                <label htmlFor="dose" className="text-gray-500 2xl:text-[11px] text-[9px]">
                                    {isDilaudid ? getDosePlaceholder(index + 1) : qtyLabel}
                                </label>
                                {/* WinRx schedule-quantity checkbox intentionally hidden.
                                {showWinRxSelection && (
                                    <label
                                        className={`group flex items-center justify-center ${hasOtherWinRxSelection ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                                        title={hasOtherWinRxSelection
                                            ? 'Clear the selected WinRx dose before choosing this dose'
                                            : dr.send_to_winrx
                                                ? 'Remove this dose from the WinRx schedule'
                                                : 'Use this dose as the WinRx schedule quantity'}
                                        aria-label={hasOtherWinRxSelection
                                            ? `Dose ${index + 1} WinRx schedule quantity unavailable`
                                            : dr.send_to_winrx
                                                ? `Remove dose ${index + 1} from the WinRx schedule`
                                                : `Use dose ${index + 1} as the WinRx schedule quantity`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={dr.send_to_winrx === true}
                                            disabled={hasOtherWinRxSelection}
                                            onChange={() => {
                                                const shouldSelect = dr.send_to_winrx !== true
                                                setFrequencyRows(prevRows => {
                                                    const newRows = [...prevRows]
                                                    const newDoseRows = (newRows[rowIndex].dose_rows || []).map((doseRow, doseIndex) => ({
                                                        ...doseRow,
                                                        send_to_winrx: shouldSelect && doseIndex === index
                                                    }))
                                                    newRows[rowIndex] = { ...newRows[rowIndex], dose_rows: newDoseRows }
                                                    if (row.frequency) {
                                                        newRows[rowIndex] = {
                                                            ...newRows[rowIndex],
                                                            [`stored_${row.frequency}`]: {
                                                                dose_rows: newDoseRows.map(doseRow => ({ ...doseRow })),
                                                                days: row.days || ''
                                                            }
                                                        }
                                                    }
                                                    return newRows
                                                })
                                            }}
                                            className="sr-only"
                                        />
                                        <span
                                            aria-hidden="true"
                                            className={`relative h-3 w-3 rounded-[3px] border ${dr.send_to_winrx ? 'border-blue-600 bg-blue-600 after:absolute after:left-[3px] after:top-[1px] after:h-[7px] after:w-[4px] after:rotate-45 after:border-b-2 after:border-r-2 after:border-white' : hasOtherWinRxSelection ? 'border-gray-300 bg-gray-100 group-hover:border-red-500 group-hover:bg-red-50' : 'border-gray-400 bg-white'}`}
                                        />
                                    </label>
                                )} */}
                            </div>
                            <div className={`flex overflow-hidden border rounded-md h-[42px] ${doseErrors[index] ? 'border-red-500' : needsDoseCode && !doseCode ? 'border-red-500' : 'border-gray-300'}`}>
                                <input
                                    type="text"
                                    value={dose}
                                    onChange={(e) => {
                                        const value = e.target.value
                                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                            setFrequencyRows(prevRows => {
                                                const newRows = [...prevRows]
                                                const newDoseRows = (newRows[rowIndex].dose_rows || []).map((r, i) => i === index ? { ...r, dose: value } : { ...r })
                                                newRows[rowIndex] = { ...newRows[rowIndex], dose_rows: newDoseRows }
                                                if (row.frequency) {
                                                    newRows[rowIndex] = {
                                                        ...newRows[rowIndex],
                                                        [`stored_${row.frequency}`]: {
                                                            dose_rows: newDoseRows.map(r => ({ ...r })),
                                                            days: row.days || ''
                                                        }
                                                    }
                                                }
                                                return newRows
                                            })
                                        }
                                    }}
                                    className="w-full min-w-0 px-2 py-1 text-gray-700 focus:ring-2 focus:ring-transparent focus:outline-none border-none bg-white"
                                />
                                {needsDoseCode && (
                                    <IvCodeDropdown
                                        value={doseCode || null}
                                        options={doseCodeOptions}
                                        placeholder=""
                                        allowClear={false}
                                        onChange={(nextValue) => {
                                            const next = nextValue || ''
                                            if (dr.id != null && typeof onUpdateDoseCode === 'function') {
                                                onUpdateDoseCode({ id: dr.id, dose_code: next || null })
                                            }
                                            setFrequencyRows(prevRows => {
                                                const newRows = [...prevRows]
                                                const newDoseRows = (newRows[rowIndex].dose_rows || []).map((r, i) => i === index ? { ...r, dose_code: next } : { ...r })
                                                newRows[rowIndex] = { ...newRows[rowIndex], dose_rows: newDoseRows }
                                                if (row.frequency) {
                                                    newRows[rowIndex] = {
                                                        ...newRows[rowIndex],
                                                        [`stored_${row.frequency}`]: {
                                                            dose_rows: newDoseRows.map(r => ({ ...r })),
                                                            days: row.days || ''
                                                        }
                                                    }
                                                }
                                                return newRows
                                            })
                                        }}
                                    />
                                )}
                                {safeSupply && (
                                    <IvCodeDropdown
                                        value={code || null}
                                        options={ivCodeOptions}
                                        onChange={(nextValue) => {
                                            const value = sanitizeIvCode(nextValue)
                                            const next = value || null
                                            if (dr.id != null && typeof onUpdateIvCode === 'function') {
                                                onUpdateIvCode({ id: dr.id, iv_code: next })
                                            }
                                            setFrequencyRows(prevRows => {
                                                const newRows = [...prevRows]
                                                const newDoseRows = (newRows[rowIndex].dose_rows || []).map((r, i) => i === index ? { ...r, iv_code: next } : { ...r })
                                                newRows[rowIndex] = { ...newRows[rowIndex], dose_rows: newDoseRows }
                                                if (row.frequency) {
                                                    newRows[rowIndex] = {
                                                        ...newRows[rowIndex],
                                                        [`stored_${row.frequency}`]: {
                                                            dose_rows: newDoseRows.map(r => ({ ...r })),
                                                            days: row.days || ''
                                                        }
                                                    }
                                                }
                                                return newRows
                                            })
                                        }}
                                    />
                                )}
                            </div>
                            {doseErrors[index] && (
                                <span className="text-red-700 font-semibold text-xs mt-1">{doseErrors[index]}</span>
                            )}
                        </div>
                    )
                })}

                {removeDays ? null : row.frequency && (
                    <div className="flex flex-col w-[100px] min-w-[100px] max-w-[100px]">
                        <label htmlFor="days" className="text-gray-500 2xl:text-[11px] text-[9px] uppercase">Days</label>
                        <input
                            type="text"
                            value={row.days || ''}
                            onChange={(e) => setFrequencyRows(prevRows => {
                                const newRows = [...prevRows]
                                newRows[rowIndex] = { ...newRows[rowIndex], days: e.target.value }
                                if (row.frequency) {
                                    const dose_rows = (newRows[rowIndex].dose_rows || []).map(r => ({ ...r }))
                                    newRows[rowIndex] = {
                                        ...newRows[rowIndex],
                                        [`stored_${row.frequency}`]: {
                                            dose_rows,
                                            days: e.target.value
                                        }
                                    }
                                }
                                return newRows
                            })}
                            className="w-full px-2 py-2 text-gray-700 font-normal border border-gray-300 h-[42px] rounded focus:outline-none focus:ring-2 focus:ring-transparent"
                        />
                    </div>
                )}

                {/* The gap is the spacing between the doses to its left, so it
                    reads as the last column of the same row. One value covers
                    the prescription, so it is drawn on the first row only
                    rather than repeated down a changing-dose plan. */}
                {rowIndex === 0 && (
                    <DoseGapField
                        frequencyRows={frequencyRows}
                        setFrequencyRows={setFrequencyRows}
                        deliveryGap={patientDetails?.get_requency_data?.delivery_gap}
                        rxesId={patientDetails?.rxdata_id}
                    />
                )}

                {removeDays ? null : row.frequency && (
                    <div className="flex items-end gap-1 2xl:gap-2">

                        {rowIndex === frequencyRows.length - 1 && (
                            <button onClick={() => handleAddRow(setFrequencyRows, frequencyRows)}
                                className={`bg-blue-600 text-white py-1 rounded h-[42px] flex justify-center items-center text-lg 2xl:w-[60px] w-[40px] 2xl:px-4 px-2  hover:bg-blue-500`}>
                                +
                            </button>
                        )}
                        {rowIndex > 0 && (
                            <button onClick={() => handleRemoveRow(rowIndex, setFrequencyRows, frequencyRows, row.cycle_no, patientDetails?.rxdata_id)}
                                className={`bg-red-500 text-white py-1 rounded h-[42px] hover:bg-red-400 flex justify-center items-center text-lg 2xl:w-[60px] w-[40px] 2xl:px-4 px-2 `}>
                                -
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* {safeSupply && !hideIvCodeUpdatedBy && row.iv_code_updated_by ? (
                <p className="text-xs italic text-green-700">
                    IV code updated by: {row.iv_code_updated_by}
                </p>
            ) : null} */}

            {safeSupply && !hideIvCodeUpdatedBy && row?.iv_code_updated_by && (
                <div className="col-span-12 w-full text-[9px] text-gray-600 italic mt-1">
                    IV code updated by: {typeof row.iv_code_updated_by === 'object' && row.iv_code_updated_by?.init_name != null
                        ? row.iv_code_updated_by.init_name
                        : (typeof row.iv_code_updated_by === 'object' && row.iv_code_updated_by?.name ? row.iv_code_updated_by.name : String(row.iv_code_updated_by ?? ''))}
                </div>
            )}

        </div>
    ))
}


export const isSingleFrequency = (frequencyRows, is_dilaudid) => {
    // return frequencyRows?.length === 1 && frequencyRows[0]?.frequency === 'OD' && frequencyRows[0]?.cycle_no !== ''
    return frequencyRows?.length === 1 && frequencyRows[0]?.frequency === 'OD' && is_dilaudid && (frequencyRows[0]?.dose_rows || []).some(dr => dr.dose !== '' && dr.dose != null)
}



export const handleRepharseNotes = (rxid, patient_id, textValue, dispatch, setPatientNotes) => {

    if (!textValue) {
        showToast("Please enter some text before rephrasing.", "error")
        return;
    }

    // Extract prescription number (like #491390) from the text
    const rxNumberMatch = textValue.match(/#\d+/);
    const rxNumber = rxNumberMatch ? rxNumberMatch[0] : '';

    // Remove prescription number from text before sending to API
    const textWithoutRxNumber = textValue.replace(/#\d+/, '').trim();

    // Validate if the text only contains a prescription number and no other text
    if (textWithoutRxNumber === '') {
        showToast("Please enter some text in addition to the prescription number.", "error")
        return;
    }

    const reqData = {
        rxid,
        patient_id,
        drp_text: textWithoutRxNumber, // Send text without prescription number
        repharseNotes: true
    };

    dispatch(handleUpdateRephraseNotes({
        reqData,
        requestType: 'notes-rephrase',
        setPatientNotes,
        rxNumber // Pass prescription number to be appended later
    }));
};

export const handleRepharseTemplateNotes = (rxid, patient_id, textValue, dispatch, setTemplateName, text = 'template-rephrase') => {

    if (!textValue) {
        showToast("Please enter some text before rephrasing.", "error")
        return;
    }

    const reqData = {
        rxid,
        patient_id,
        drp_text: textValue, // Send text without prescription number
        repharseNotes: true
    };

    dispatch(handleUpdateTemplateNotes({
        reqData,
        requestType: text,
        setTemplateName,
    }));
};

export const normalizeDinGroupDrug = (drug) => {
    const dinValue = String(
        drug?.din ??
        drug?.DGDIN ??
        ''
    ).trim();

    const brandName = String(
        drug?.brand_name ??
        drug?.name ??
        drug?.DGDESC ??
        ''
    ).trim();

    return {
        ...drug,
        din: dinValue,
        brand_name: brandName,
        dosage_form_badge: String(drug?.dosage_form_badge ?? '').trim(),
    };
};

export const formatDosageFormBadge = (value) => {
    return String(value ?? '').split('(')[0].trim();
};

export const filterDinGroupDrugs = (rows, currentDin = '') => {
    const normalizedCurrentDin = String(currentDin || '').trim();
    const seenDins = new Set();

    return (Array.isArray(rows) ? rows : [])
        .map(normalizeDinGroupDrug)
        .filter((drug) => {
            if (!drug?.din) return false;
            if (normalizedCurrentDin && drug.din === normalizedCurrentDin) return false;
            if (seenDins.has(drug.din)) return false;

            const hasDisplayName = Boolean(String(drug.brand_name || drug.name || '').trim());
            if (!hasDisplayName) return false;

            seenDins.add(drug.din);
            return true;
        });
};


// ✅ Validate WinRx and AI Stop Date Mismatch
export const validateWinRxAiStopDateMismatch = (patientDetails, formData) => {
    const winRxStopDate = patientDetails?.date_range?.stop_date;
    const aiStopDate = formData?.stop_date;

    if (!winRxStopDate || !aiStopDate) return false;

    // Check if both dates exist
    if (winRxStopDate && aiStopDate) {
        // Calculate expected AI stop date (WinRx stop date - 1 day)
        const expectedAiStopDateStr = shiftRxDate(winRxStopDate, -1);
        if (!expectedAiStopDateStr) return false;

        // Return true if AI stop date matches expected, false otherwise
        return getValidRxDate(aiStopDate) !== expectedAiStopDateStr;
    }
    return true;

};


// Add this function to src/utils/helper/investigationRx.js
export const checkLowStock = (prescription_detail, rxStockData) => {
    let authQty;
    const rxdrug = prescription_detail?.rxdrug?.toLowerCase();
    const refills_authorized = prescription_detail?.refills_authorized;

    // Check if "methadone" exists anywhere in the rxdrug string, case-insensitive
    if (rxdrug && (rxdrug.includes('methadone') || rxdrug.includes('methadose'))) {
        return false;
    }

    if (refills_authorized > 0) {
        authQty = refills_authorized * prescription_detail?.day_supply;
    } else {
        authQty = prescription_detail?.quantity_authorized;
    }

    if (rxStockData >= 0 && authQty) {
        return rxStockData < authQty;
    }

    return false;
};

// ✅ Stock To Come Message Component
export const StockToComeMessage = ({ prescription_detail, rxStockData }) => {
    const isLowStock = checkLowStock(prescription_detail, rxStockData);

    if (!isLowStock) return null;

    return (
        <div className="text-[9px] text-red-500 font-medium mt-1 whitespace-nowrap uppercase">
            Stock to come
        </div>
    );
};

// ✅ Update Schedule Dose Helper Function
export const handleUpdateScheduleDoseHelper = (rxNumber, quantity, dispatch, setUpdatedScheduleDose, setDispenseInfo) => {
    if (!rxNumber || !quantity) {
        showToast('quantity required', 'error');
        return;
    }

    const payload = {
        rx: rxNumber,
        quantity: quantity
    };

    dispatch(handleUpdateScheduleDose({
        payload,
        setUpdatedScheduleDose,
        setDispenseInfo
    }));
};
